import React, { useEffect, useRef, useState, useCallback } from "react";
import { widget } from "./charting_library";
import Datafeed, { resetReplay } from "./datafeed_custom";
import BacktestBroker from "./BacktestBroker";
import HtmlOrderOverlay from "./HtmlOrderOverlay";
import PostTradeAnalysis from './Posttradeanalysis ';

// ══════════════════════════════════════════════════════════════════════════════
// 🗄️ SESSION MANAGER – IndexedDB-backed replay session persistence
// Sessions expire after 2 hours (free) or 24 hours (premium) from CREATION time.
// Free users are limited to 4 session starts. Premium = unlimited.
// ══════════════════════════════════════════════════════════════════════════════
const SESSION_DB_NAME = 'backtestx_sessions';
const SESSION_STORE = 'sessions';
// Expiry is measured from session.createdAt (NOT savedAt)
const FREE_SESSION_EXPIRY_MS = 2 * 60 * 60 * 1000; // 2 hours  (free)
const PREM_SESSION_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours (premium)
const FREE_SESSION_LIMIT = 4;                         // max active sessions for free users

const openSessionDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(SESSION_DB_NAME, 1);
  req.onupgradeneeded = (e) => {
    const db = e.target.result;
    if (!db.objectStoreNames.contains(SESSION_STORE)) {
      db.createObjectStore(SESSION_STORE, { keyPath: 'id' });
    }
  };
  req.onsuccess = (e) => resolve(e.target.result);
  req.onerror = (e) => reject(e.target.error);
});

const SessionDB = {
  async save(session) {
    const db = await openSessionDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(SESSION_STORE, 'readwrite');
      tx.objectStore(SESSION_STORE).put(session);
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e.target.error);
    });
  },
  async getAll() {
    const db = await openSessionDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(SESSION_STORE, 'readonly');
      const req = tx.objectStore(SESSION_STORE).getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = (e) => reject(e.target.error);
    });
  },
  async delete(id) {
    const db = await openSessionDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(SESSION_STORE, 'readwrite');
      tx.objectStore(SESSION_STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e.target.error);
    });
  },
  async purgeExpired() {
    const all = await this.getAll();
    const now = Date.now();
    for (const s of all) {
      if (now - s.savedAt > SESSION_EXPIRY_MS) {
        await this.delete(s.id);
      }
    }
  },
  getExpiryMs(session, isPremium) {
    const expiryMs = isPremium ? PREM_SESSION_EXPIRY_MS : FREE_SESSION_EXPIRY_MS;
    // Expiry measured from CREATION time, not last save
    const origin = session.createdAt || session.savedAt || 0;
    return Math.max(0, expiryMs - (Date.now() - origin));
  },
  formatTimeLeft(ms) {
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}m ${s}s`;
  }
};

if (typeof window !== 'undefined') {
  const originalError = console.error;
  console.error = (...args) => {
    if (args[0]?.includes?.('ResizeObserver loop completed')) return;
    originalError.apply(console, args);
  };
}

const TVChartContainer = () => {
  const chartContainerRef = useRef(null);
  const tvWidgetRef = useRef(null);
  const replayLineIdRef = useRef(null);
  const recursiveTimerRef = useRef(null);
  const isRenderingRef = useRef(false);
  const busyStartTimeRef = useRef(null);
  const activeIntervalRef = useRef("5"); // 🔥 ACTIVE INTERVAL TRACKER
  const tfSwitchTokenRef = useRef(0);

  // ===============================
  // 📰 NEWS SYSTEM (TRADINGVIEW STYLE)
  // ===============================
  const newsShapesRef = useRef([]);     // store icon ids
  const newsIndexRef = useRef(0);       // pointer for replay sync
  const newsLoadedRef = useRef(false);
  const newsRenderLastTimeRef = useRef(0); // 🔥 time-gate for renderNewsUpToTime calls
  // 🔥 MAKE NEWS INDEX GLOBAL (for datafeed sync)
  useEffect(() => {
    window.newsIndexRef = newsIndexRef;
  }, []);

  // prevent reload spam
  const tfSwitchTimerRef = useRef(null);
  const [isReplayUIVisible, setIsReplayUIVisible] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(1000);
  // 🔥 NEW: Replay Step Resolution (like TraderCasa)
  const [replayStep, setReplayStep] = useState("1");
  const [showStepDropdown, setShowStepDropdown] = useState(false);

  // values: "1", "5", "15", "60"

  const [balance, setBalance] = useState(0);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [balanceAction, setBalanceAction] = useState("add");
  const [balanceAmount, setBalanceAmount] = useState("");
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [showNewsPanel, setShowNewsPanel] = useState(false);

  // 📝 CHECKLIST FEATURE STATES
  const [showChecklistPanel, setShowChecklistPanel] = useState(false);
  const [checklistItems, setChecklistItems] = useState([]); // { text, checked }
  const [isEditingChecklist, setIsEditingChecklist] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState("");
  // 📋 TRADE CHECKLIST POPUP (shown after placing a trade)
  const [showTradeChecklistPopup, setShowTradeChecklistPopup] = useState(false);
  const [tradeChecklistSelections, setTradeChecklistSelections] = useState([]); // { text, ticked }
  const [pendingTradeForChecklist, setPendingTradeForChecklist] = useState(null); // stores trade id
  // 📰 PROFESSIONAL NEWS FILTER STATES
  const [newsMode, setNewsMode] = useState("auto"); // auto | manual
  const [manualFilter, setManualFilter] = useState("upcoming"); // upcoming | today | high
  const [filteredNews, setFilteredNews] = useState([]);
  const [activeTab, setActiveTab] = useState("order");
  const [currentReplayPrice, setCurrentReplayPrice] = useState(0);
  const [openTrades, setOpenTrades] = useState([]);
  const [orderType, setOrderType] = useState("market");
  const [side, setSide] = useState(1);
  const [limitPrice, setLimitPrice] = useState("");
  const [lotSize, setLotSize] = useState(1);
  const [leverage, setLeverage] = useState(1);
  const [tpInput, setTpInput] = useState("");
  const [slInput, setSlInput] = useState("");
  const [showModeSelection, setShowModeSelection] = useState(false);
  const [replayAlert, setReplayAlert] = useState({ show: false, message: "" });
  // 🔔 PROFESSIONAL CHART NOTIFICATIONS (TRADINGVIEW STYLE)
  const [chartNotifications, setChartNotifications] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(() => {
    return localStorage.getItem("backtestx_currency") || "USD";
  });
  // 🏆 PROP CHALLENGE STATES
  const [activePropChallenge, setActivePropChallenge] = useState(null);
  const [propStartBalance, setPropStartBalance] = useState(0);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  const CURRENCY_CONFIG = {
    USD: { symbol: "$", rate: 1, label: "USD" },
    INR: { symbol: "₹", rate: 83, label: "INR" },
    EUR: { symbol: "€", rate: 0.92, label: "EUR" },
    GBP: { symbol: "£", rate: 0.79, label: "GBP" },
    JPY: { symbol: "¥", rate: 149, label: "JPY" },
    AUD: { symbol: "A$", rate: 1.53, label: "AUD" },
    CAD: { symbol: "C$", rate: 1.36, label: "CAD" },
    CHF: { symbol: "Fr", rate: 0.90, label: "CHF" },
    SGD: { symbol: "S$", rate: 1.34, label: "SGD" },
    AED: { symbol: "د.إ", rate: 3.67, label: "AED" },
  };
  useEffect(() => {
    localStorage.setItem("backtestx_currency", selectedCurrency);
  }, [selectedCurrency]);
  // Helper shortcuts (use these everywhere instead of isUSD checks)
  const currSymbol = CURRENCY_CONFIG[selectedCurrency]?.symbol ?? "$";
  const currRate = CURRENCY_CONFIG[selectedCurrency]?.rate ?? 1;
  const toDisplayAmount = (usdAmount) => usdAmount * currRate;
  const triggerAlert = (msg) => {
    setReplayAlert({ show: true, message: msg });
    setTimeout(() => setReplayAlert({ show: false, message: "" }), 3000);
  };
  // ==========================================
  // 🔔 TRADINGVIEW STYLE CHART NOTIFICATION
  // ==========================================
  const showChartNotification = (text, type = "info") => {
    const id = Date.now();

    const colors = {
      success: "#089981", // green
      error: "#f23645",   // red
      warning: "#ffc107", // yellow
      info: "#2962ff"     // blue
    };

    const newNotification = {
      id,
      text,
      color: colors[type] || colors.info
    };

    setChartNotifications(prev => [...prev, newNotification]);

    // Auto remove after 3 seconds (like real trading apps)
    setTimeout(() => {
      setChartNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };
  // '' = auto by symbol (VERY IMPORTANT)
  const [spread, setSpread] = useState('');
  // ⭐ NEW: Commission (for Stocks & Indices)
  const [commission, setCommission] = useState(''); // default 0.2%
  // 🌍 USER SELECTED UTC (PERSISTENT TIMEZONE)
  const [userUTC, setUserUTC] = useState(() => {
    const saved = localStorage.getItem("backtestx_utc");
    return saved !== null ? parseFloat(saved) : 5.5; // default = India (IST)
  });

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [closedTradeData, setClosedTradeData] = useState(null);
  const tfSwitchingRef = useRef(false);
  const floatingControlsRef = useRef(null);
  const dragStateRef = useRef({ dragging: false, offsetX: 0, offsetY: 0 });
  const dragHandleRef = useRef(null); // ⭐ NEW (grip dots)
  const dragDataRef = useRef({
    isDragging: false,
    offsetX: 0,
    offsetY: 0,
  });
  // ======================================
  // ⭐ ASSET TYPE DETECTOR (PRO BROKER LOGIC)
  // ======================================
  // 🔥 NORMALIZE SYMBOL (VERY IMPORTANT FOR BACKTESTX)
  const normalizeSymbol = (symbol) => {
    if (!symbol) return "XAUUSD";

    return symbol
      .replace(/_REPLAY/g, "")
      .replace(/[\/\-_]/g, "") // removes / - _
      .toUpperCase()
      .trim();
  };

  const getAssetType = (symbol) => {
    if (!symbol) return "forex";

    const s = normalizeSymbol(symbol);

    // =========================
    // 💱 FOREX (MAJORS + MINORS + EXOTICS)
    // =========================
    const forexPairs = [
      // Majors
      "EURUSD", "GBPUSD", "AUDUSD", "NZDUSD",
      "USDJPY", "USDCHF", "USDCAD",

      // Cross pairs
      "EURJPY", "GBPJPY", "EURGBP", "EURAUD", "EURCAD", "EURCHF", "EURNZD",
      "GBPAUD", "GBPCAD", "GBPCHF", "GBPNZD",
      "AUDJPY", "AUDCAD", "AUDCHF", "AUDNZD",
      "CADJPY", "CADCHF", "CHFJPY", "NZDJPY", "NZDCAD", "NZDCHF",

      // Exotics (important for your app library)
      "USDINR", "USDCNY", "USDHKD", "USDSGD", "USDTHB", "USDKRW",
      "USDZAR", "USDMXN", "USDBRL", "USDTRY", "USDRUB",
      "USDSEK", "USDNOK", "USDDKK", "USDPLN", "USDHUF", "USDCZK",
      "EURSEK", "EURNOK", "EURDKK", "EURPLN", "EURHUF", "EURCZK",
      "GBPSEK", "GBPNOK", "AUDSGD", "AUDHKD", "SGDJPY", "HKDJPY"
    ];

    // =========================
    // 🟡 METALS (TREATED AS FOREX - SPREAD MODEL)
    // =========================
    const metals = [
      "XAUUSD", "XAUEUR", "XAUGBP",
      "XAGUSD",
      "XPTUSD", // Platinum (your data pipeline)
      "XPDUSD"  // Palladium
    ];

    // =========================
    // 🪙 CRYPTO
    // =========================
    const crypto = [
      "BTCUSD", "ETHUSD", "XRPUSD", "SOLUSD", "BNBUSD",
      "ADAUSD", "DOGEUSD", "TRXUSD", "LINKUSD", "AVAXUSD"
    ];

    // =========================
    // 📈 INDICES
    // =========================
    const indices = [
      "SPX", "NDX", "DJI", "VIX", "DAX", "NIKKEI",
      "NIFTY", "BANKNIFTY"
    ];

    // =========================
    // 🚀 DETECTION LOGIC (ORDER MATTERS)
    // =========================

    // Metals → behave like forex (important for your cost engine)
    if (metals.includes(s)) return "forex";

    // Forex pairs
    if (forexPairs.includes(s)) return "forex";

    // Crypto
    if (crypto.includes(s)) return "crypto";

    // Indices
    if (indices.includes(s)) return "index";

    // Smart auto-detect fallback (VERY useful for replay symbols)
    if (s.startsWith("XAU") || s.startsWith("XAG") || s.startsWith("XPT") || s.startsWith("XPD")) {
      return "forex";
    }

    if (s.endsWith("USD") && s.length === 6) {
      // Likely forex or crypto pair like EURUSD, BTCUSD
      if (crypto.includes(s)) return "crypto";
      return "forex";
    }

    // Default fallback = stock (AAPL, TSLA, RELIANCE, etc.)
    return "stock";
  };

  // ======================================
  // 🔥 UNIVERSAL CONTRACT SIZE (BROKER-REALISTIC)
  // Used for: Margin + PnL + Risk Calculation
  // ======================================
  const LOT_MULTIPLIER = {
    // =========================
    // 🟡 METALS (CFD STANDARD)
    // =========================
    "XAUUSD": 100,
    "XAU-USD": 100,
    "XAUEUR": 100,
    "XAU-EUR": 100,
    "XAUGBP": 100,
    "XAU-GBP": 100,

    "XAGUSD": 5000,
    "XAG-USD": 5000,

    "XPTUSD": 50,   // Platinum (lower liquidity, realistic CFD size)
    "XPT-USD": 50,
    "XPDUSD": 50,   // Palladium
    "XPD-USD": 50,

    // =========================
    // 💱 FOREX (STANDARD LOT = 100,000)
    // =========================
    "EURUSD": 100000,
    "EUR-USD": 100000,
    "GBPUSD": 100000,
    "GBP-USD": 100000,
    "AUDUSD": 100000,
    "AUD-USD": 100000,
    "NZDUSD": 100000,
    "NZD-USD": 100000,
    "USDCAD": 100000,
    "USD-CAD": 100000,
    "USDCHF": 100000,
    "USD-CHF": 100000,

    // Cross pairs
    "EURGBP": 100000,
    "EUR-GBP": 100000,
    "EURAUD": 100000,
    "EUR-AUD": 100000,
    "EURCAD": 100000,
    "EUR-CAD": 100000,
    "EURCHF": 100000,
    "EUR-CHF": 100000,
    "EURNZD": 100000,
    "EUR-NZD": 100000,
    "GBPAUD": 100000,
    "GBP-AUD": 100000,
    "GBPCAD": 100000,
    "GBP-CAD": 100000,
    "GBPCHF": 100000,
    "GBP-CHF": 100000,
    "GBPNZD": 100000,
    "GBP-NZD": 100000,
    "AUDCAD": 100000,
    "AUD-CAD": 100000,
    "AUDCHF": 100000,
    "AUD-CHF": 100000,
    "AUDNZD": 100000,
    "AUD-NZD": 100000,
    "NZDCAD": 100000,
    "NZD-CAD": 100000,
    "NZDCHF": 100000,
    "NZD-CHF": 100000,

    // =========================
    // 💴 JPY PAIRS (different pip structure)
    // =========================
    "USDJPY": 1000,
    "USD-JPY": 1000,
    "EURJPY": 1000,
    "EUR-JPY": 1000,
    "GBPJPY": 1000,
    "GBP-JPY": 1000,
    "AUDJPY": 1000,
    "AUD-JPY": 1000,
    "CADJPY": 1000,
    "CAD-JPY": 1000,
    "CHFJPY": 1000,
    "CHF-JPY": 1000,
    "NZDJPY": 1000,
    "NZD-JPY": 1000,
    "SGDJPY": 1000,
    "SGD-JPY": 1000,
    "HKDJPY": 1000,
    "HKD-JPY": 1000,

    // =========================
    // 🌏 EXOTIC FOREX (smaller contract realism)
    // =========================
    "USDINR": 1000,
    "USD-INR": 1000,
    "USDCNY": 10000,
    "USD-CNY": 10000,
    "USDHKD": 10000,
    "USD-HKD": 10000,
    "USDSGD": 10000,
    "USD-SGD": 10000,
    "USDTHB": 10000,
    "USD-THB": 10000,
    "USDKRW": 10000,
    "USD-KRW": 10000,
    "USDZAR": 10000,
    "USD-ZAR": 10000,
    "USDMXN": 10000,
    "USD-MXN": 10000,
    "USDBRL": 10000,
    "USD-BRL": 10000,
    "USDTRY": 10000,
    "USD-TRY": 10000,
    "USDRUB": 10000,
    "USD-RUB": 10000,
    "USDSEK": 10000,
    "USD-SEK": 10000,
    "USDNOK": 10000,
    "USD-NOK": 10000,
    "USDDKK": 10000,
    "USD-DKK": 10000,
    "USDPLN": 10000,
    "USD-PLN": 10000,
    "USDHUF": 10000,
    "USD-HUF": 10000,
    "USDCZK": 10000,
    "USD-CZK": 10000,

    // =========================
    // 🪙 CRYPTO (CFD STYLE)
    // =========================
    "BTCUSD": 1,
    "BTC-USD": 1,
    "ETHUSD": 1,
    "ETH-USD": 1,
    "XRPUSD": 1,
    "XRP-USD": 1,
    "SOL-USD": 1,
    "BNB-USD": 1,
    "ADA-USD": 1,
    "DOGE-USD": 1,
    "TRX-USD": 1,
    "LINK-USD": 1,
    "AVAX-USD": 1,

    // =========================
    // 📈 INDICES (INDEX CFD STYLE)
    // =========================
    "SPX": 1,
    "NDX": 1,
    "DJI": 1,
    "VIX": 1,
    "DAX": 1,
    "NIKKEI": 1,

    // Indian Indices (important for BacktestX users)
    "NIFTY": 50,
    "BANKNIFTY": 15,

    // =========================
    // 🏢 STOCKS (1 LOT = 1 SHARE)
    // =========================
    "AAPL": 1,
    "MSFT": 1,
    "NVDA": 1,
    "TSLA": 1,
    "GOOGL": 1,
    "AMZN": 1,
    "META": 1,
    "NFLX": 1,
    "AMD": 1,
    "JPM": 1,
    "V": 1,

    // Indian Stocks
    "RELIANCE": 1,
    "HDFCBANK": 1,
    "TCS": 1,
    "INFY": 1,
  };
  // ======================================
  // 💰 DEFAULT COST MODEL (PRO BROKER STYLE)
  // Spread = price units (NOT pips)
  // Commission = %
  // ======================================
  const DEFAULT_COST = {
    // 🟡 METALS (Realistic CFD spreads)
    "XAUUSD": { spread: 0.2, commission: 0 },
    "XAGUSD": { spread: 0.03, commission: 0 },
    "XAUEUR": { spread: 0.25, commission: 0 },
    "XAUGBP": { spread: 0.25, commission: 0 },
    "XPTUSD": { spread: 0.6, commission: 0 },   // Platinum (wider liquidity)
    "XPDUSD": { spread: 1.0, commission: 0 },   // Palladium (very wide IRL)

    // 💱 MAJOR FOREX (Ultra tight)
    "EURUSD": { spread: 0.0002, commission: 0 },
    "GBPUSD": { spread: 0.0003, commission: 0 },
    "USDJPY": { spread: 0.02, commission: 0 },
    "AUDUSD": { spread: 0.0003, commission: 0 },
    "NZDUSD": { spread: 0.0004, commission: 0 },
    "USDCAD": { spread: 0.0003, commission: 0 },
    "USDCHF": { spread: 0.0003, commission: 0 },

    // 💱 MINOR / CROSS PAIRS
    "EURJPY": { spread: 0.03, commission: 0 },
    "GBPJPY": { spread: 0.05, commission: 0 },
    "EURGBP": { spread: 0.0003, commission: 0 },
    "EURAUD": { spread: 0.0006, commission: 0 },
    "EURCAD": { spread: 0.0006, commission: 0 },
    "EURCHF": { spread: 0.0005, commission: 0 },
    "EURNZD": { spread: 0.0008, commission: 0 },
    "GBPAUD": { spread: 0.0009, commission: 0 },
    "GBPCAD": { spread: 0.0008, commission: 0 },
    "GBPCHF": { spread: 0.0007, commission: 0 },
    "GBPNZD": { spread: 0.0010, commission: 0 },
    "AUDJPY": { spread: 0.04, commission: 0 },
    "AUDCAD": { spread: 0.0006, commission: 0 },
    "AUDCHF": { spread: 0.0006, commission: 0 },
    "AUDNZD": { spread: 0.0007, commission: 0 },
    "CADJPY": { spread: 0.04, commission: 0 },
    "CADCHF": { spread: 0.0006, commission: 0 },
    "CHFJPY": { spread: 0.04, commission: 0 },
    "NZDJPY": { spread: 0.05, commission: 0 },
    "NZDCAD": { spread: 0.0007, commission: 0 },
    "NZDCHF": { spread: 0.0007, commission: 0 },

    // 🌏 EXOTIC PAIRS (Wider spreads for realism)
    "USDINR": { spread: 0.02, commission: 0 },
    "USDCNY": { spread: 0.01, commission: 0 },
    "USDHKD": { spread: 0.005, commission: 0 },
    "USDSGD": { spread: 0.003, commission: 0 },
    "USDTHB": { spread: 0.05, commission: 0 },
    "USDKRW": { spread: 1.5, commission: 0 },
    "USDZAR": { spread: 0.02, commission: 0 },
    "USDMXN": { spread: 0.015, commission: 0 },
    "USDBRL": { spread: 0.03, commission: 0 },
    "USDTRY": { spread: 0.05, commission: 0 },
    "USDRUB": { spread: 0.08, commission: 0 },
    "USDSEK": { spread: 0.01, commission: 0 },
    "USDNOK": { spread: 0.01, commission: 0 },
    "USDDKK": { spread: 0.008, commission: 0 },
    "USDPLN": { spread: 0.02, commission: 0 },
    "USDHUF": { spread: 0.5, commission: 0 },
    "USDCZK": { spread: 0.03, commission: 0 },

    // 🇪🇺 EURO EXOTICS
    "EURSEK": { spread: 0.012, commission: 0 },
    "EURNOK": { spread: 0.012, commission: 0 },
    "EURDKK": { spread: 0.01, commission: 0 },
    "EURPLN": { spread: 0.025, commission: 0 },
    "EURHUF": { spread: 0.6, commission: 0 },
    "EURCZK": { spread: 0.04, commission: 0 },

    // 🇬🇧 GBP EXOTICS
    "GBPSEK": { spread: 0.02, commission: 0 },
    "GBPNOK": { spread: 0.02, commission: 0 },

    // 🌏 ASIA CROSSES
    "AUDSGD": { spread: 0.004, commission: 0 },
    "AUDHKD": { spread: 0.006, commission: 0 },
    "SGDJPY": { spread: 0.05, commission: 0 },
    "HKDJPY": { spread: 0.06, commission: 0 },

    // 🪙 CRYPTO (exchange style)
    "BTCUSD": { spread: 5, commission: 0.05 },
    "ETHUSD": { spread: 2, commission: 0.05 },
    "XRPUSD": { spread: 0.01, commission: 0.05 },

    // 📈 INDICES (commission model)
    "NIFTY": { spread: 0, commission: 0.02 },
    "BANKNIFTY": { spread: 0, commission: 0.02 },
    "SPX": { spread: 0, commission: 0.02 },
    "NDX": { spread: 0, commission: 0.02 },

    // 🏢 STOCKS
    "AAPL": { spread: 0, commission: 0.1 },
    "TSLA": { spread: 0, commission: 0.1 },
  };


  const replayModeTypeRef = useRef(null);
  const sessionAutoSaveRef = useRef(null);
  const currentSessionIdRef = useRef(null);

  // ── Session UI States ──────────────────────────────────────────────────────
  const [savedSessions, setSavedSessions] = useState([]);
  const [showSessionPopup, setShowSessionPopup] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  // (showSaveSessionPrompt removed – no popup before starting replay)

  const speeds = [3000, 2000, 1000, 500, 200, 100, 50, 25, 10, 5];

  const speedLabels = {
    3000: "0.2x",
    2000: "0.5x",
    1000: "1x",
    500: "2x",
    200: "5x",
    100: "10x",
    50: "20x",
    25: "40x",
    10: "100x",
    5: "200x"
  };

  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("backtestx_theme");
    return savedTheme ? savedTheme === "dark" : true; // default dark
  });
  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const newTheme = !prev;

      // Apply theme to TradingView chart
      if (tvWidgetRef.current) {
        tvWidgetRef.current.changeTheme(newTheme ? "Dark" : "Light");
      }

      // 🔥 SAVE THEME (THIS FIXES YOUR BUG)
      localStorage.setItem("backtestx_theme", newTheme ? "dark" : "light");

      return newTheme;
    });
  };
  const handleSliderChange = (e) => {
    const index = parseInt(e.target.value);
    setReplaySpeed(speeds[index]);
  };

  const currentSpeedIndex = speeds.indexOf(replaySpeed);
  // ── Load premium status + sessions on mount ────────────────────────────
  useEffect(() => {
    const initSessions = async () => {
      try {
        // ⭐ Check premium via WebBridge (firebaseBridge.js already has Firebase running)
        let userIsPremium = false;
        try {
          if (window.WebBridge?.checkUserPremium) {
            userIsPremium = await window.WebBridge.checkUserPremium();
          } else if (window._userIsPremium !== undefined) {
            userIsPremium = window._userIsPremium;
          }
        } catch (e) {
          console.warn('Premium check failed, defaulting to free:', e);
        }
        setIsPremium(userIsPremium);

        // Purge sessions using correct expiry for this user's plan
        const expiryMs = userIsPremium ? PREM_SESSION_EXPIRY_MS : FREE_SESSION_EXPIRY_MS;
        const all = await SessionDB.getAll();
        for (const s of all) {
          const origin = s.createdAt || s.savedAt || 0;
          if (Date.now() - origin > expiryMs) {
            await SessionDB.delete(s.id);
          }
        }

        const sessions = await SessionDB.getAll();
        const valid = sessions.filter(s => {
          const origin = s.createdAt || s.savedAt || 0;
          return Date.now() - origin < expiryMs;
        });
        if (valid.length > 0) {
          setSavedSessions(valid.sort((a, b) => b.savedAt - a.savedAt));
          setShowSessionPopup(true);
        }
      } catch (e) {
        console.warn('Session DB init failed:', e);
      }
    };
    initSessions();
  }, []);

  // ── Auto-save session every 30s when replay is active ─────────────────────
  const autoSaveSession = useCallback(async () => {
    if (!window.replayMode) return;
    try {
      const symbol = tvWidgetRef.current?.symbolInterval()?.symbol || 'XAUUSD';
      const cleanSymbol = symbol.replace('_REPLAY', '');
      const replayIdx = window.replayIndex ?? 0;
      const replayTime = window.replayEnd ?? 0;
      const mode = replayModeTypeRef.current || 'manual';

      const sessionId = currentSessionIdRef.current || `session_${Date.now()}`;
      currentSessionIdRef.current = sessionId;

      // Get existing session to PRESERVE its createdAt (expiry should not reset on save)
      let createdAt = Date.now();
      try {
        const existing = await SessionDB.getAll();
        const prev = existing.find(s => s.id === sessionId);
        if (prev?.createdAt) createdAt = prev.createdAt;
      } catch (_) { }

      const session = {
        id: sessionId,
        symbol: cleanSymbol,
        replayIndex: replayIdx,
        replayTime,
        balance,
        mode,
        createdAt,            // ⭐ FIXED: expiry based on CREATION, not last save
        savedAt: Date.now(),  // display / sorting only
        label: `${cleanSymbol} – ${new Date(createdAt).toLocaleString()}`,
      };
      await SessionDB.save(session);

      // Update local state so it appears in popup immediately without reload
      setSavedSessions(prev => {
        const filtered = prev.filter(s => s.id !== sessionId);
        return [session, ...filtered].sort((a, b) => b.savedAt - a.savedAt);
      });

      console.log('💾 Session auto-saved (id:', sessionId, ')');
    } catch (e) {
      console.warn('Session auto-save failed:', e);
    }
  }, [balance]);

  useEffect(() => {
    if (isReplayUIVisible) {
      sessionAutoSaveRef.current = setInterval(autoSaveSession, 30000);
    } else {
      if (sessionAutoSaveRef.current) {
        clearInterval(sessionAutoSaveRef.current);
        sessionAutoSaveRef.current = null;
      }
    }
    return () => {
      if (sessionAutoSaveRef.current) clearInterval(sessionAutoSaveRef.current);
    };
  }, [isReplayUIVisible, autoSaveSession]);

  // ⭐ CALLED FROM ANDROID AFTER AD (OR IF FAILED)
  useEffect(() => {
    window.openReplayPopup = () => {
      console.log("🎬 Opening replay popup after ad");
      setShowModeSelection(true);
    };
  }, []);
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".currency-dropdown-wrapper")) {
        setShowCurrencyDropdown(false);
      }
    };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);
  // ==========================================
  // 🔥 NEW: FIREBASE BALANCE SYNC LISTENER
  // ==========================================
  // --- 1. Initialize balance from AndroidBridge/Firebase ---
  // ==========================================
  // 🔥 BALANCE INIT (ANDROID + WEB)
  // ==========================================
  useEffect(() => {

    const loadBalance = async () => {

      try {
        // 🔥 PROP CHALLENGE OVERRIDE 🔥
        const urlParams = new URLSearchParams(window.location.search);
        const propId = urlParams.get("prop");

        if (propId && window.WebBridge?.listenPropChallenge) {
          window.WebBridge.listenPropChallenge(propId, (propData) => {
            if (propData) {
              setActivePropChallenge(propData);
              
              const fundingStr = propData.fundingSize ? propData.fundingSize.replace(/k|K/g, '000').replace(/,/g, '') : "10000";
              const parsedFunding = parseFloat(fundingStr) || 10000;
              
              setPropStartBalance(propData.startingBalance || parsedFunding);
              
              if (propData.balance != null) {
                setBalance(propData.balance);
              } else {
                setBalance(propData.startingBalance || parsedFunding);
              }
            }
          });
          return; // ⬅️ skip normal wallet balance load
        }

        // ANDROID APP
        if (window.AndroidBridge?.getFirebaseAmount) {

          const amount = window.AndroidBridge.getFirebaseAmount();

          console.log("📱 Balance loaded (Android):", amount);

          setBalance(amount);

          return;
        }

        // WEB DASHBOARD
        if (window.WebBridge?.getFirebaseAmount) {

          const amount = await window.WebBridge.getFirebaseAmount();

          console.log("🌐 Balance loaded (Web):", amount);

          setBalance(amount);

          // realtime listener
          window.WebBridge.listenBalance((newAmount) => {

            console.log("💰 Balance updated:", newAmount);

            setBalance(newAmount);

          });

        }

      } catch (err) {

        console.error("Balance load failed:", err);

      }

    };

    loadBalance();

  }, []);

  const updateTradesUI = () => {
    if (window.brokerInstance) {
      setOpenTrades([...window.brokerInstance._orders]);
      setBalance(window.brokerInstance._balance);
    }
  };

  const updateBalanceSafe = (newAmount) => {

    setBalance(newAmount);

    // 🔥 PROP CHALLENGE MODE 🔥
    const urlParams = new URLSearchParams(window.location.search);
    const propId = urlParams.get("prop");
    if (propId && window.WebBridge?.updatePropChallenge) {
      window.WebBridge.updatePropChallenge(propId, { balance: newAmount });
      return;
    }

    // Android
    if (window.AndroidBridge?.saveNewAmount) {

      console.log("📱 Saving balance:", newAmount);

      window.AndroidBridge.saveNewAmount(newAmount);

      return;
    }

    // Web
    if (window.WebBridge?.saveNewAmount) {

      console.log("🌐 Saving balance:", newAmount);

      window.WebBridge.saveNewAmount(newAmount);

    }

  };
  useEffect(() => {
    const closeDropdown = (e) => {
      if (!e.target.closest(".step-dropdown")) {
        setShowStepDropdown(false);
      }
    };

    window.addEventListener("click", closeDropdown);
    return () => window.removeEventListener("click", closeDropdown);
  }, []);
  // ✅ PERSIST USER UTC IN LOCALSTORAGE
  useEffect(() => {
    try {
      localStorage.setItem("backtestx_utc", String(userUTC));
    } catch (e) {
      console.warn("Failed to save UTC:", e);
    }
  }, [userUTC]);

  // ==========================================
  // 🔥 HEARTBEAT & RECOVERY LOGIC
  // ==========================================
  useEffect(() => {
    const heartbeat = setInterval(() => {
      const status = window.getReplayStatus?.();

      // replay not running → ignore
      if (!status?.mode) return;

      // during controlled TF switch → ignore
      if (window._tfSwitching === true) return;

      // waiting means realtime callback not attached yet
      if (status?.wait && !status?.ready) {
        console.warn("💓 [HEARTBEAT] Waiting for subscriber...", status);
      }
    }, 7000);

    return () => clearInterval(heartbeat);
  }, []);
  useEffect(() => {
    window.replayStepSize = parseInt(replayStep) || 1;
  }, [replayStep]);

  // ==========================================
  // 🎯 DRAG WITH HANDLE (PRO UX)
  // Only drag when grabbing the dots icon
  // ==========================================
  // ==========================================
  // 🎯 HYBRID DRAG (MOUSE + TOUCH) – PRO VERSION
  // Works on:
  // ✔ Desktop mouse
  // ✔ Mobile touch (Android APK / WebView)
  // ✔ Only drags from handle (not slider/buttons)
  // ==========================================
  useEffect(() => {
    const panel = floatingControlsRef.current;
    const handle = dragHandleRef.current;

    if (!panel || !handle) return;

    const drag = dragDataRef.current;

    // ===== START DRAG (MOUSE) =====
    const onMouseDown = (e) => {
      drag.isDragging = true;

      const rect = panel.getBoundingClientRect();
      drag.offsetX = e.clientX - rect.left;
      drag.offsetY = e.clientY - rect.top;

      handle.style.cursor = "grabbing";
      e.preventDefault();
    };

    // ===== START DRAG (TOUCH) =====
    const onTouchStart = (e) => {
      const touch = e.touches[0];
      drag.isDragging = true;

      const rect = panel.getBoundingClientRect();
      drag.offsetX = touch.clientX - rect.left;
      drag.offsetY = touch.clientY - rect.top;
    };

    // ===== MOVE (MOUSE) =====
    const onMouseMove = (e) => {
      if (!drag.isDragging) return;

      const wrapper = chartContainerRef.current?.parentElement;
      if (!wrapper) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();

      let x = e.clientX - drag.offsetX - wrapperRect.left;
      let y = e.clientY - drag.offsetY - wrapperRect.top;

      // 🔒 CLAMP INSIDE CHART BOUNDS (MAIN FIX)
      const maxX = wrapperRect.width - panelRect.width - 10;
      const maxY = wrapperRect.height - panelRect.height - 10;

      x = Math.max(10, Math.min(x, maxX));
      y = Math.max(10, Math.min(y, maxY));

      panel.style.left = `${x}px`;
      panel.style.top = `${y}px`;
    };
    const onTouchMove = (e) => {
      if (!drag.isDragging) return;

      const touch = e.touches[0];
      const wrapper = chartContainerRef.current?.parentElement;
      if (!wrapper) return;

      const wrapperRect = wrapper.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();

      let x = touch.clientX - drag.offsetX - wrapperRect.left;
      let y = touch.clientY - drag.offsetY - wrapperRect.top;

      // 🔒 CLAMP INSIDE CHART (CRITICAL FOR ANDROID APK)
      const maxX = wrapperRect.width - panelRect.width - 10;
      const maxY = wrapperRect.height - panelRect.height - 10;

      x = Math.max(10, Math.min(x, maxX));
      y = Math.max(10, Math.min(y, maxY));

      panel.style.left = `${x}px`;
      panel.style.top = `${y}px`;
    };

    // ===== STOP DRAG =====
    const stopDrag = () => {
      drag.isDragging = false;
      if (handle) handle.style.cursor = "grab";
    };

    // 🎯 Attach ONLY to drag handle (NOT full panel)
    handle.addEventListener("mousedown", onMouseDown);
    handle.addEventListener("touchstart", onTouchStart, { passive: true });

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopDrag);

    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", stopDrag);

    return () => {
      handle.removeEventListener("mousedown", onMouseDown);
      handle.removeEventListener("touchstart", onTouchStart);

      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopDrag);

      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", stopDrag);
    };
  }, [isReplayUIVisible]);

  // ==========================================
  // REPLAY LOOP
  // ==========================================
  useEffect(() => {
    let isMounted = true;
    // 🔥 ADAPTIVE STEP ENGINE (ULTRA FAST)
    // 🔥 TF-AWARE REALISTIC MULTI-STEP ENGINE
    // 🔥 REALISTIC SPEED ENGINE (NO TF BOOST)
    // Always 1m internal stepping like TraderCasa
    const getStepsForSpeed = (delayMs) => {
      if (delayMs >= 1000) return 1;      // 1x
      if (delayMs >= 500) return 2;       // 2x
      if (delayMs >= 200) return 4;       // 5x
      if (delayMs >= 100) return 8;       // 10x
      if (delayMs >= 50) return 12;       // 20x
      if (delayMs >= 25) return 20;       // 40x
      if (delayMs >= 10) return 40;       // 100x
      return 60;                          // 200x
    };
    const runRecursiveTick = () => {
      if (!isPlaying || !isMounted) return;

      window.requestAnimationFrame(() => {
        if (!isMounted || !isPlaying) return;

        const status = window.getReplayStatus?.();

        if (isRenderingRef.current || status?.busy) {
          if (!busyStartTimeRef.current) busyStartTimeRef.current = Date.now();

          if (Date.now() - busyStartTimeRef.current > 4000) {
            isRenderingRef.current = false;
            if (window.prepareDatafeedForReplay) window.prepareDatafeedForReplay();
            busyStartTimeRef.current = null;
          }

          recursiveTimerRef.current = setTimeout(runRecursiveTick, 50);
          return;
        }

        busyStartTimeRef.current = null;

        // 🔥 BIG PERFORMANCE UPGRADE (MULTI STEP PER FRAME)
        // 🔥 SINGLE STEP ENGINE (ReplayStep controls ticks like TraderCasa)
        // 🔥 SPEED + REPLAY STEP COMBINED (CORRECT)
        // ✅ Only ONE tick per frame (datafeed step controls jump size)
        const speedSteps = getStepsForSpeed(replaySpeed);

        // ✅ only speed controls how many "step actions" per frame
        for (let i = 0; i < speedSteps; i++) {
          window.executeNextStep?.(); // executeNextStep will handle stepSize internally
        }




        // 🔥 LOWER MIN DELAY = SMOOTHER FAST SPEED
        const safeDelay = Math.max(replaySpeed, 25); // 16ms = 60fps
        recursiveTimerRef.current = setTimeout(() => {
          if (isMounted && isPlaying) runRecursiveTick();
        }, safeDelay);
      });
    };


    if (isPlaying) runRecursiveTick();
    return () => {
      isMounted = false;
      if (recursiveTimerRef.current) clearTimeout(recursiveTimerRef.current);
    };
  }, [isPlaying, replaySpeed]);
  // ==========================================
  // CHART INITIALIZATION
  // ==========================================
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const symbol = urlParams.get("symbol") || "XAUUSD";
    const interval = urlParams.get("timeframe") || "5";

    const tvWidget = new widget({
      symbol,
      interval: 1,
      datafeed: Datafeed,
      container: chartContainerRef.current,
      timezone: localStorage.getItem("backtestx_tv_timezone") || "Etc/UTC",
      library_path: "/terminal/charting_library/",
      locale: "en",
      theme: localStorage.getItem("backtestx_theme") === "light" ? "Light" : "Dark",
      autosize: true,
      broker_factory: (host) => new BacktestBroker(host),
      charts_storage_url: null,
      charts_storage_api_version: "1.1",
      client_id: "backtestx",
      user_id: "default",

      news_provider: function (symbol, callback) {
        const tryProvide = () => {
          const news = Array.isArray(window.newsEvents) ? window.newsEvents : [];
          const newsItems = news.map(event => ({
            title: event.title || "Economic News",
            published: Math.floor(event.time / 1000),
            source: event.impact === 3 ? "HIGH IMPACT"
              : event.impact === 2 ? "MEDIUM IMPACT"
                : "LOW IMPACT",
            shortDescription:
              (event.impact === 3 ? "🔴 High Impact"
                : event.impact === 2 ? "🟡 Medium Impact"
                  : "🔵 Low Impact") +
              ` | ${new Date(event.time).toUTCString()}`,
            link: "",
            urgency: event.impact === 3 ? 2
              : event.impact === 2 ? 1
                : 0,
          }));
          callback({ newsItems });
        };

        // 🔥 SAVE CALLBACK GLOBALLY so we can re-trigger after news loads
        window._tvNewsCallback = tryProvide;

        if (window.newsEvents?.length > 0) {
          tryProvide();
        } else {
          const poll = setInterval(() => {
            if (window.newsEvents?.length > 0) {
              clearInterval(poll);
              tryProvide();
            }
          }, 500);
          setTimeout(() => clearInterval(poll), 15000);
        }
      },

      supported_resolutions: ['1', '2', '3', '5', '10', '15', '30', '60', '120', '180', '240', '1D'],
      supports_marks: true,

      overrides: {
        "paneProperties.background": isDarkMode ? "#131722" : "#ffffff",
        "paneProperties.backgroundType": "solid",
      },

      favorites: {
        intervals: ["1"],
      },
      enabled_features: [
        // Header & core controls
        "header_widget",
        "header_symbol_search",
        "header_compare",
        "header_indicators",
        "header_chart_type",
        "header_resolutions",
        "header_settings",
        "header_undo_redo",
        "header_screenshot",

        // Drawings & tools
        "show_object_tree",
        "drawing_templates",
        "line_tools_grouping",
        "magnet",

        // Legend & values
        "legend_widget",
        "study_legend_widget",

        // UX power
        "keyboard_shortcuts",
        "context_menus",
        "symbol_search_hot_key",
        "use_localstorage_for_settings",
        "items_favoriting",

        // Layouts
        "multiple_charts_layouts",

        // Looks more “real TV”
        "border_around_the_chart",
        "display_market_status",
        "multiple_charts_layouts",
        "trading_account_manager",
        "open_account_manager",
        "dom_widget",
        "trading_notifications",
        "side_toolbar_in_fullscreen_mode",
        "buy_sell_buttons",
        "study_templates",
        "save_chart_properties_to_local_storage",
        "news_headlines",
      ],

      disabled_features: [
        // You already built your own
        "header_saveload",
        "header_undo_redo",
        "header_screenshot",
        "header_compare", // 🔥 THIS removes Save/Load layout button
        "go_to_date",
      ],

      save_load_adapter: {

        // ── Chart Layout ──────────────────────────────
        getAllCharts: async () => {
          const saved = localStorage.getItem("tv_chart_layout");
          return saved ? [JSON.parse(saved)] : [];
        },

        saveChart: async (chartData) => {
          chartData.id = chartData.id || "default_chart";

          // ✅ CLEAN REPLAY STATE BEFORE SAVING
          try {
            const content = typeof chartData.content === "string"
              ? JSON.parse(chartData.content)
              : chartData.content;

            if (content) {
              // 1. Remove _REPLAY suffix from symbol
              if (content.symbol) {
                content.symbol = content.symbol.replace("_REPLAY", "");
              }

              // 2. Remove replay-related panes/series data
              if (content.charts) {
                content.charts = content.charts.map(chart => {
                  if (chart.panes) {
                    chart.panes = chart.panes.map(pane => {
                      if (pane.sources) {
                        pane.sources = pane.sources.map(source => {
                          if (source.state?.symbol) {
                            source.state.symbol = source.state.symbol.replace("_REPLAY", "");
                          }
                          return source;
                        });
                      }
                      return pane;
                    });
                  }
                  return chart;
                });
              }

              chartData.content = JSON.stringify(content);
            }
          } catch (e) {
            console.warn("Layout clean error:", e);
          }

          localStorage.setItem("tv_chart_layout", JSON.stringify(chartData));
          return chartData.id;
        },

        getChartContent: async (id) => {
          const saved = localStorage.getItem("tv_chart_layout");
          if (!saved) return null;

          try {
            const parsed = JSON.parse(saved);
            if (!parsed.content) return null;

            // ✅ PARSE AND STRIP OLD VISIBLE RANGE
            const content = typeof parsed.content === "string"
              ? JSON.parse(parsed.content)
              : parsed.content;

            if (content?.charts) {
              content.charts = content.charts.map(chart => {

                // 🔥 RESET visible range to current year (prevents old BIN requests)
                if (chart.timeScale) {
                  delete chart.timeScale.m_barSpacing;
                  delete chart.timeScale.m_rightOffset;

                  // Force range to recent 3 months only
                  const now = Math.floor(Date.now() / 1000);
                  const threeMonthsAgo = now - (60 * 60 * 24 * 90);

                  chart.timeScale.m_from = threeMonthsAgo;
                  chart.timeScale.m_to = now;
                }

                // 🔥 ALSO CLEAR PANE RANGES (TV stores per-pane visible range)
                if (chart.panes) {
                  chart.panes = chart.panes.map(pane => {
                    if (pane.leftAxisState) delete pane.leftAxisState;
                    if (pane.rightAxisState) delete pane.rightAxisState;
                    return pane;
                  });
                }

                return chart;
              });
            }

            return JSON.stringify(content);

          } catch (e) {
            console.warn("Layout load clean error:", e);
            // fallback: return raw content
            const parsed = JSON.parse(saved);
            return parsed.content ?? null;
          }
        },

        removeChart: async (id) => {
          localStorage.removeItem("tv_chart_layout");
        },

        // ── Study / Indicator Templates (unchanged) ───
        getAllStudyTemplates: async () => {
          const saved = localStorage.getItem("tv_study_templates");
          return saved ? JSON.parse(saved) : [];
        },

        saveStudyTemplate: async (templateData) => {
          const saved = localStorage.getItem("tv_study_templates");
          const templates = saved ? JSON.parse(saved) : [];
          const index = templates.findIndex(t => t.name === templateData.name);
          if (index >= 0) {
            templates[index] = templateData;
          } else {
            templates.push(templateData);
          }
          localStorage.setItem("tv_study_templates", JSON.stringify(templates));
          return templateData.name;
        },

        getStudyTemplateContent: async (templateData) => {
          const saved = localStorage.getItem("tv_study_templates");
          const templates = saved ? JSON.parse(saved) : [];
          const name = typeof templateData === "string" ? templateData : templateData.name;
          const found = templates.find(t => t.name === name);
          return found ? found.content : null;
        },

        removeStudyTemplate: async (templateData) => {
          const saved = localStorage.getItem("tv_study_templates");
          const templates = saved ? JSON.parse(saved) : [];
          const name = typeof templateData === "string" ? templateData : templateData.name;
          const filtered = templates.filter(t => t.name !== name);
          localStorage.setItem("tv_study_templates", JSON.stringify(filtered));
        },
      }
    });
    tvWidgetRef.current = tvWidget;
    window.tvWidgetRef = tvWidgetRef;   // ⭐ ADD THIS

    tvWidget.onChartReady(() => {
      tvWidget.activeChart().refreshMarks();

      // 🌍 SAVE CURRENT TIMEZONE (SAFE – NO INVALID API)
      // 🌍 FORCE RESTORE + AUTO TRACK TIMEZONE (FINAL FIX)
      try {
        const chart = tvWidget.activeChart();

        // 1️⃣ Restore saved timezone properly
        const savedTz = localStorage.getItem("backtestx_tv_timezone");
        if (savedTz) {
          chart.setTimezone(savedTz);
          console.log("🌍 Restored TZ:", savedTz);
        }
        // ✅ AUTO SAVE on indicator/drawing change
        tvWidget.subscribe('onAutoSaveNeeded', () => {
          tvWidget.saveChartToServer();
        });
        // 2️⃣ Poll timezone changes (TradingView has NO native listener)
        let lastTz = chart.getTimezone?.() || "Etc/UTC";

        setInterval(() => {
          try {
            const currentTz = chart.getTimezone?.() || "Etc/UTC";
            if (currentTz !== lastTz) {
              lastTz = currentTz;
              localStorage.setItem("backtestx_tv_timezone", currentTz);
              console.log("🌍 Timezone Saved:", currentTz);
            }
          } catch (err) { }
        }, 1000); // lightweight check

      } catch (e) {
        console.warn("Timezone sync failed:", e);
      }


      // ======================================
      // 💰 AUTO DEFAULT SPREAD & COMMISSION (FIXED)
      // ======================================
      const applyCostBySymbol = () => {
        try {
          const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol;
          if (!rawSymbol) return;

          const activeSymbol = normalizeSymbol(rawSymbol);
          const config = DEFAULT_COST[activeSymbol];

          console.log("💰 Symbol detected:", activeSymbol);

          if (config) {
            console.log("💰 Applying DEFAULT COST:", config);

            // FORCE set defaults (not conditional)
            setSpread(config.spread);
            setCommission(config.commission);
          } else {
            // Smart fallback by asset type
            const assetType = getAssetType(activeSymbol);

            if (activeSymbol.includes("XAU")) {
              setSpread(0.2);
              setCommission(0);
            }
            else if (assetType === "forex") {
              setSpread(0.0002);
              setCommission(0);
            }
            else if (assetType === "crypto") {
              setSpread(5);
              setCommission(0.05);
            }
            else if (assetType === "index") {
              setSpread(0);
              setCommission(0.02);
            }
            else {
              setSpread(0);
              setCommission(0.1);
            }
          }
        } catch (e) {
          console.error("❌ Cost auto-apply failed:", e);
        }
      };

      // APPLY ON FIRST LOAD
      applyCostBySymbol();

      // APPLY WHEN SYMBOL CHANGES (CRITICAL)
      const tvChart = tvWidgetRef.current.activeChart();
      tvChart.onSymbolChanged().subscribe(null, () => {
        setTimeout(() => {
          applyCostBySymbol();
        }, 100);
      });

      // ===============================
      // 📰 RENDER NEWS ICONS (LIKE TRADINGVIEW)
      // ===============================

      // ⭐ EXPORT AFTER FUNCTION (VERY IMPORTANT)


      // 🔥 LISTEN FOR INTERVAL CHANGES (FIX SWITCHING LAG)
      const chart = tvWidget.activeChart();
      activeIntervalRef.current = chart.resolution();
      chart.onIntervalChanged().subscribe(null, (interval) => {
        if (!window.replayMode) return;
        // 🚨 THE KEY FIX FOR THE 10-SECOND DELAY 🚨
        // TV internally debounces subscriptions — it keeps the OLD TF subscription
        // alive for ~10 seconds before calling unsubscribeBars. During that window,
        // TV skips calling getBars for the new TF entirely.
        //
        // Fix: call _onResetCacheNeeded() IMMEDIATELY at the start of the switch.
        // This is the callback TV gave us in subscribeBars(). Calling it tells TV:
        // "your bar cache is stale, IMMEDIATELY re-call getBars + subscribeBars!"
        // TV responds within milliseconds — the 10-second wait disappears completely.
        if (typeof window._onResetCacheNeeded === "function") {
          try {
            window._onResetCacheNeeded();
            console.log("⚡ [TF SWITCH] Instant cache reset fired for:", interval);
          } catch (e) {
            console.warn("[TF SWITCH] onResetCacheNeeded failed:", e);
          }
          // Don't null it yet — unsubscribeBars will clean it up when TV fires it
        }

        // ✅ latest TF change wins (kills stale delayed handlers)
        tfSwitchTokenRef.current += 1;
        const token = tfSwitchTokenRef.current;

        // 🔥 RACE GUARD: bump so in-flight subscribeBars for old TF are rejected
        window._tfSwitchSeq = (window._tfSwitchSeq || 0) + 1;
        const mySeq = window._tfSwitchSeq;

        // cancel previous pending timer / poll
        if (tfSwitchTimerRef.current) clearTimeout(tfSwitchTimerRef.current);

        // 🔥 mark switching
        tfSwitchingRef.current = true;

        // 🔥 update desired TF immediately (prevents stale TF replay)
        activeIntervalRef.current = interval;
        window._desiredResolution = interval;

        // 🔥 Save the CURRENT live callback as pre-switch backup BEFORE we null it.
        window._preSwitchCallback = window._onRealtimeCallback || window._lastOnRealtimeCallback || null;
        window._preSwitchUID = window._lastSubscriberUID || null;

        // 🔥 force engine to wait until new subscriber attaches
        window._isReadyForRealtime = false;

        // Kill old callback right away so stale pump can't emit mid-switch.
        window._onRealtimeCallback = null;

        // 🔥 Clear the main fallback slot: subscribeBars for NEW TF repopulates it.
        window._lastOnRealtimeCallback = null;
        window._lastSubscriberUID = null;

        // 🔥 also update activeSubscriber resolution instantly
        if (window._activeSubscriber) {
          window._activeSubscriber.resolution = interval;
        }

        const wasPlaying = isPlaying;
        setIsPlaying(false);

        console.log("🔄 TF Switching to:", interval, "seq=", mySeq);

        // kill old callback/subscriber immediately
        window._hardKillSubscriber?.();

        // 🔥 POLL-BASED SUBSCRIBER DETECTION + FORCE-RETRY RECOVERY
        // Problem: On fast TF switches (< 10s), TV skips calling getBars and
        // subscribeBars entirely. After 10s it unsubscribes the old TF via its
        // own timeout, but the cache-reset signal in unsubscribeBars doesn't
        // reliably trigger re-fetch. The poll detects this stuck state and forces
        // recovery via chart.setResolution(interval) → re-triggers getBars flow.
        const pollStartTime = Date.now();
        let retried = false; // only force-retry once per switch

        const pollForSubscriber = () => {
          if (token !== tfSwitchTokenRef.current) return; // superseded by newer switch
          if (mySeq !== window._tfSwitchSeq) return;      // another TF change happened

          if (window._isReadyForRealtime) {
            // Subscriber is attached – safe to resume playback
            tfSwitchingRef.current = false;
            if (wasPlaying) setIsPlaying(true);
            console.log("✅ TF subscriber ready, resuming play. seq=", mySeq);
            return;
          }

          const elapsed = Date.now() - pollStartTime;

          // 🔥 RECOVERY: After 12s of waiting without a subscriber, TV has
          // almost certainly already unsubscribed the old TF (its timeout is ~10s)
          // but failed to re-fetch. Force it by calling setResolution again.
          // We do this exactly ONCE so we don't loop forever.
          if (!retried && elapsed > 12000) {
            retried = true;
            console.warn(
              `⚠️ [POLL] No subscriber after ${elapsed}ms, forcing chart.setResolution(${interval})`
            );
            try {
              // Re-fire the resolution. TV will call getBars + subscribeBars fresh.
              // Our _preSwitchCallback is still saved so the self-subscribe fallback
              // in getBars will activate the pump immediately if subscribeBars lags.
              const activeChart = tvWidgetRef.current?.activeChart?.();
              if (activeChart) {
                activeChart.setResolution(interval, () => {
                  console.log("✅ [POLL] Forced setResolution complete, seq=", mySeq);
                });
              }
            } catch (e) {
              console.warn("[POLL] setResolution recovery failed:", e);
            }
          }

          // Keep polling (will exit early once _isReadyForRealtime flips)
          tfSwitchTimerRef.current = setTimeout(pollForSubscriber, 80);
        };

        // Give TV a brief head-start to call getBars before first poll check
        tfSwitchTimerRef.current = setTimeout(pollForSubscriber, 80);
      });
      const updateEntryLinePnL = (trade, currentPrice) => {
        if (!trade?.tvLines?.entryLine) return;

        const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol || "XAUUSD";
        const activeSymbol = normalizeSymbol(rawSymbol);

        const lotValue = LOT_MULTIPLIER[activeSymbol] ?? 1;

        let rawPnl = 0;

        if (trade.isExecuted) {
          rawPnl =
            (currentPrice - trade.price) *
            trade.side *
            trade.qty *
            lotValue;

          // ⭐ ADD THIS (COMMISSION FOR STOCKS & INDICES)
          const assetType = getAssetType(activeSymbol);
          if (assetType === "stock" || assetType === "index") {
            const tradeValue = trade.price * trade.qty * lotValue;
            const commissionCost = (tradeValue * (commission === '' ? 0.2 : commission)) / 100;
            rawPnl -= commissionCost;
          }
        }

        const pnl = toDisplayAmount(rawPnl);
        const currency = currSymbol;

        const baseText = `${trade.side === 1 ? "BUY" : "SELL"} ${trade.qty} (LIVE)`;

        let color = "#d1d4dc";
        if (pnl > 0) color = "#089981";
        if (pnl < 0) color = "#f23645";

        trade.tvLines.entryLine
          .setText(`${baseText}  ${pnl >= 0 ? "+" : ""}${currency}${pnl.toFixed(2)}`)
          .setLineColor(color)
          .setBodyTextColor(color);
      };
      window.executeNextStep = () => {
        if (!window.pumpNextBar || isRenderingRef.current) return null;

        try {
          isRenderingRef.current = true;

          // 🔥 TF-AWARE MULTI STEP (TraderCasa style)
          const stepSize = window.replayStepSize || 1;
          let bar = null;

          for (let i = 0; i < stepSize; i++) {
            const steppedBar = window.pumpNextBar();
            if (!steppedBar) break;

            // ⭐ CRITICAL: Check TP/SL on EVERY candle (not just last)
            const high = steppedBar.high;
            const low = steppedBar.low;
            const close = steppedBar.close;

            setOpenTrades(prevTrades => {
              const activeTrades = [];

              for (let trade of prevTrades) {
                let shouldClose = false;
                let closeReason = "";

                // Skip pending limit orders (they are handled separately)
                if (!trade.isExecuted) {
                  activeTrades.push(trade);
                  continue;
                }

                const tp = trade.tvLines?.tpLine?.getPrice?.() ?? null;
                const sl = trade.tvLines?.slLine?.getPrice?.() ?? null;

                if (tp != null || sl != null) {
                  // 🔥 TRUE WICK LOGIC (HIGH/LOW, not close/body)
                  const hitTP = tp != null && (
                    trade.side === 1
                      ? high >= tp   // BUY TP hit by wick
                      : low <= tp    // SELL TP hit by wick
                  );

                  const hitSL = sl != null && (
                    trade.side === 1
                      ? low <= sl    // BUY SL hit by wick
                      : high >= sl   // SELL SL hit by wick
                  );

                  if (hitTP) {
                    shouldClose = true;
                    closeReason = "TP";
                  } else if (hitSL) {
                    shouldClose = true;
                    closeReason = "SL";
                  }
                }

                if (shouldClose) {
                  // mark for closure (your existing close logic below will handle PnL)
                  trade._closeByEngine = true;
                  trade._closeReason = closeReason;
                }

                activeTrades.push(trade);
              }

              return activeTrades;
            });

            // keep last stepped bar for UI/render
            bar = steppedBar;
          }

          if (!bar) {
            isRenderingRef.current = false;
            return null;
          }
          // 🧠 Only check news every 3 candles (huge performance boost)

          const currentPrice = bar.close;
          const high = bar.high;
          const low = bar.low;
          setCurrentReplayPrice(currentPrice);
          // 🔥 Live PnL on entry line
          setOpenTrades(prev => {
            prev.forEach(trade => {
              if (trade.isExecuted) {
                updateEntryLinePnL(trade, currentPrice);
              }
            });
            return prev;
          });

          setOpenTrades(prevTrades => {
            const activeTrades = [];
            for (let trade of prevTrades) {
              let shouldClose = false;
              let closeReason = "";
              const entryPrice = trade.tvLines?.entryLine?.getPrice?.() ?? trade.price;

              // TP/SL may be null for pending LIMIT trades
              const tpPrice = trade.tvLines?.tpLine?.getPrice?.() ?? null;
              const slPrice = trade.tvLines?.slLine?.getPrice?.() ?? null;


              if (!trade.isExecuted && trade.orderType === "limit") {

                const limitTriggerPrice = trade.price;

                const priceNowHigh = high;
                const priceNowLow = low;


                // 🔥 Use candle wick for realistic limit fills (like real brokers)
                const isEntryHit = trade.side === 1
                  ? low <= limitTriggerPrice   // BUY LIMIT filled if wick touches
                  : high >= limitTriggerPrice; // SELL LIMIT filled if wick touches

                if (isEntryHit) {
                  // 🔥 LOCK MARGIN (CRITICAL FIX)
                  if (!trade._lockedMargin) {
                    trade._lockedMargin = trade.marginUsed;
                  }
                  trade.isExecuted = true;
                  // 🔥 AUTO ADD TP/SL WHEN LIMIT BECOMES LIVE (PRO LOGIC)
                  if (!trade.tpPrice && !trade.slPrice) {
                    const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol || "XAUUSD";
                    const activeSymbol = normalizeSymbol(rawSymbol);

                    const lotValue = LOT_MULTIPLIER[activeSymbol] ?? 1;

                    const entry = trade.price;

                    const tpPrice = trade.pendingTP
                      ? parseFloat(trade.pendingTP)
                      : (trade.side === 1 ? entry * 1.01 : entry * 0.99);

                    const slPrice = trade.pendingSL
                      ? parseFloat(trade.pendingSL)
                      : (trade.side === 1 ? entry * 0.99 : entry * 1.01);

                    // HTML overlay will draw TP/SL lines — just save prices on the trade object
                    trade.tpPrice = tpPrice;
                    trade.slPrice = slPrice;
                  }

                  // ✅ Apply spread NOW (like real broker)
                  // ⭐ REAL BROKER ENTRY MODEL (IMPORTANT FIX)
                  const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol || "XAUUSD";
                  const activeSymbol = normalizeSymbol(rawSymbol);
                  const assetType = getAssetType(activeSymbol);

                  // ⭐ SAFE SPREAD (ENGINE LEVEL - CRITICAL FIX)
                  const safeSpread = spread === '' || spread == null ? 0 : Number(spread);

                  if (assetType === "forex" || assetType === "crypto") {
                    // Apply spread only to forex & crypto
                    trade.price = trade.side === 1
                      ? limitTriggerPrice + safeSpread
                      : limitTriggerPrice - safeSpread;
                  } else {
                    // Stocks & Indices → NO spread (commission model)
                    trade.price = limitTriggerPrice;
                  }
                }
              }

              if (trade.isExecuted && (trade.tpPrice != null || trade.slPrice != null)) {

                const tp = trade.tpPrice;
                const sl = trade.slPrice;

                // 🔥 PRO: Use candle wick instead of close (realistic fills)
                const hitTP = tp != null && (trade.side === 1
                  ? high >= tp   // BUY TP hit by wick
                  : low <= tp);  // SELL TP hit by wick

                const hitSL = sl != null && (trade.side === 1
                  ? low <= sl    // BUY SL hit by wick
                  : high >= sl); // SELL SL hit by wick

                if (hitTP) {
                  shouldClose = true;
                  closeReason = "TP";
                } else if (hitSL) {
                  shouldClose = true;
                  closeReason = "SL";
                }
              }

              if (shouldClose) {
                const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol || "XAUUSD";
                const activeSymbol = normalizeSymbol(rawSymbol);

                const lotValue = LOT_MULTIPLIER[activeSymbol] ?? 1;
                // 🔥 REALISTIC PnL (CRITICAL FIX)
                // Pending LIMIT orders must NOT show floating PnL
                // 🔥 REAL EXIT PRICE (TP/SL accurate)
                let exitPrice = currentPrice;

                if (closeReason === "TP" && trade.tpPrice != null) {
                  exitPrice = trade.tpPrice;
                } else if (closeReason === "SL" && trade.slPrice != null) {
                  exitPrice = trade.slPrice;
                }

                let rawPnl = 0;

                if (trade.isExecuted) {
                  rawPnl =
                    (exitPrice - trade.price) *
                    trade.side *
                    trade.qty *
                    lotValue;

                  // ⭐ APPLY COMMISSION FOR STOCKS & INDICES (CHANGE 2)
                  const assetType = getAssetType(activeSymbol);
                  if (assetType === "stock" || assetType === "index") {
                    const tradeValue = trade.price * trade.qty * lotValue;
                    const commissionCost = (tradeValue * (commission === '' ? 0.2 : commission)) / 100;
                    rawPnl -= commissionCost;
                  }
                }


                const pnl = toDisplayAmount(rawPnl);


                const refundAmount = trade.marginUsed + pnl;


                // 🚫 Save history ONLY in AUTO replay (NOT manual)
                const histBridge = window.AndroidBridge || window.WebBridge;
                if (
                  replayModeTypeRef.current === "auto" && // ⭐ ONLY AUTO MODE (your requirement)
                  histBridge?.saveTradeToHistory
                ) {
                  // ⭐ TP/SL prices stored directly on trade object
                  const realSL = trade.slPrice ?? null;
                  const realTP = trade.tpPrice ?? null;

                  // ⭐ SAFE LOCKED MARGIN (never lose value)
                  const usedMargin = trade._lockedMargin ?? trade.marginUsed ?? 0;

                  // ✅ HOLD TIME CALCULATION
                  const closeTime = Date.now();
                  const holdDurationMs = closeTime - (trade.openTime ?? closeTime);
                  const holdSeconds = Math.floor(holdDurationMs / 1000);
                  const holdMinutes = Math.floor(holdSeconds / 60);
                  const holdHours = Math.floor(holdMinutes / 60);
                  const holdDuration =
                    holdHours > 0
                      ? `${holdHours}h ${holdMinutes % 60}m`
                      : holdMinutes > 0
                        ? `${holdMinutes}m ${holdSeconds % 60}s`
                        : `${holdSeconds}s`;

                  const tradeData = {
                    symbol: activeSymbol,
                    type: trade.side === 1 ? "BUY" : "SELL",
                    status: closeReason,
                    pnl: Number((pnl || 0).toFixed(2)),
                    entry: Number(trade.price),
                    exit: Number(exitPrice),
                    lots: Number(trade.qty),
                    leverage: `${trade.leverage}x`,
                    sl: realSL !== null ? Number(realSL) : null,
                    tp: realTP !== null ? Number(realTP) : null,
                    marginUsed: Number(usedMargin),
                    timestamp: Date.now(),
                    openTime: Number(trade.openTime ?? closeTime),   // ✅ ADD
                    closeTime: Number(closeTime),                    // ✅ ADD
                    holdDurationMs: Number(holdDurationMs),          // ✅ ADD
                    holdDuration: holdDuration,                      // ✅ ADD
                  };

                  console.log("📤 Saving trade to Firestore:", tradeData);
                  
                  // 🔥 PROP CHALLENGE OVERRIDE 🔥
                  const urlParams = new URLSearchParams(window.location.search);
                  const propId = urlParams.get("prop");
                  if (propId && histBridge?.savePropTradeToHistory) {
                      histBridge.savePropTradeToHistory(propId, JSON.stringify(tradeData));
                  } else {
                      histBridge.saveTradeToHistory(JSON.stringify(tradeData));
                  }
                }
                // Get the current candle where user executed trade 
                const list = window.barCache["1"] || [];
                const idx = window.replayIndex;
                const tradeBarTime = list[idx]?.time ?? Date.now();


                const tradeDateObj = new Date(tradeBarTime);
                const tradeDate = tradeDateObj.toISOString().split('T')[0];
                const tradeTime = tradeDateObj.toTimeString().split(' ')[0]; // HH:MM:SS

                // ISO week calculation
                function getWeekNumber(date) {
                  const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                  const dayNum = tempDate.getUTCDay() || 7;
                  tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
                  const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
                  return Math.ceil((((tempDate - yearStart) / 86400000) + 1) / 7);
                }
                const weekOfYear = getWeekNumber(tradeDateObj);
                const year = tradeDateObj.getFullYear();

                // 🚫 Anti-cheat: send to Android ONLY in AUTO replay mode
                const actionBridge = window.AndroidBridge || window.WebBridge;
                if (
                  replayModeTypeRef.current === 'auto' &&
                  actionBridge
                ) {
                  const entryPrice = trade.price;
                  const slPrice = trade.slPrice ?? null;
                  const tpPrice = trade.tpPrice ?? null;


                  if (closeReason === "TP" && actionBridge.onTP) {
                    const currencyCode = selectedCurrency;
                    actionBridge.onTP(
                      pnl,
                      currencyCode,
                      tradeDate,
                      year,
                      weekOfYear,
                      tradeTime,
                      entryPrice,
                      slPrice ?? 0,
                      tpPrice ?? 0
                    );
                  }
                  else if (closeReason === "SL" && actionBridge.onSL) {
                    const currencyCode = selectedCurrency;
                    actionBridge.onSL(
                      pnl,
                      currencyCode,
                      tradeDate,
                      year,
                      weekOfYear,
                      tradeTime,
                      entryPrice,
                      slPrice ?? 0,
                      tpPrice ?? 0
                    );
                  }
                }
                setBalance(prevBal => {
                  // 🔔 TP / SL HIT NOTIFICATION (PRO BROKER STYLE)
                  if (closeReason === "TP") {
                    showChartNotification(
                      `🎯 Take Profit Hit | PnL: ${currSymbol}${pnl.toFixed(2)}`,
                      "success"
                    );
                    setClosedTradeData({
                      symbol: activeSymbol,
                      side: trade.side,
                      entry: trade.price,
                      exit: exitPrice,
                      tp: trade.tpPrice ?? null,
                      sl: trade.slPrice ?? null,
                      lots: trade.qty,
                      leverage: trade.leverage,
                      pnl: pnl,
                      closeReason: "TP",
                      marginUsed: trade._lockedMargin ?? trade.marginUsed ?? 0,
                      currency: selectedCurrency,
                    });
                    setIsPlaying(false); // ⬅️ ADD THIS
                  } else if (closeReason === "SL") {
                    showChartNotification(
                      `🛑 Stop Loss Hit | PnL: ${currSymbol}${pnl.toFixed(2)}`,
                      "error"
                    );
                    setClosedTradeData({
                      symbol: activeSymbol,
                      side: trade.side,
                      entry: trade.price,
                      exit: exitPrice,
                      tp: trade.tpPrice ?? null,
                      sl: trade.slPrice ?? null,
                      lots: trade.qty,
                      leverage: trade.leverage,
                      pnl: pnl,
                      closeReason: "SL",
                      marginUsed: trade._lockedMargin ?? trade.marginUsed ?? 0,
                      currency: selectedCurrency,
                    });
                    setIsPlaying(false); // ⬅️ ADD THIS
                  }
                  const finalBal = prevBal + refundAmount;
                  
                  // 🔥 PROP CHALLENGE MODE 🔥
                  const urlParams = new URLSearchParams(window.location.search);
                  const propId = urlParams.get("prop");
                  if (propId && window.WebBridge?.updatePropChallenge) {
                    window.WebBridge.updatePropChallenge(propId, { balance: finalBal });
                    return finalBal;
                  }

                  const localBridge = window.AndroidBridge || window.WebBridge;
                  if (localBridge?.saveNewAmount) {
                    localBridge.saveNewAmount(finalBal);
                  }
                  return finalBal;
                });
                // HTML overlay auto-removes when trade leaves openTrades state


              } else {
                activeTrades.push(trade);
              }
            }
            return activeTrades;
          });

          setTimeout(() => {
            isRenderingRef.current = false;
          }, 30);

          return bar;
        } catch (err) {
          isRenderingRef.current = false;
          return null;
        }
      };

      tvWidget.headerReady().then(() => {

        const replayBtn = tvWidget.createButton();
        replayBtn.innerHTML = "Replay";
        replayBtn.style.color = "#2962ff";

        replayBtn.addEventListener("click", () => {
          if (window.replayMode === true) {
            triggerAlert("Replay is already active. Exit to start a new one.");
            return;
          }

          if (window.AndroidBridge?.showReplayAd) {
            window.AndroidBridge.showReplayAd();
          } else {
            setShowModeSelection(true);
          }
        });

      });

      // ==========================================
      // 🌙 STABLE DARK MODE BUTTON (NO DOM HACKS)
      // ==========================================
      tvWidget.headerReady().then(() => {

        const themeBtn = tvWidget.createButton();
        themeBtn.setAttribute("title", "Toggle Theme");
        themeBtn.style.cursor = "pointer";

        // Create elements (NOT innerHTML → prevents reset bug)
        const wrapper = document.createElement("div");
        wrapper.style.display = "flex";
        wrapper.style.alignItems = "center";
        wrapper.style.gap = "8px";
        wrapper.style.userSelect = "none";

        const text = document.createElement("span");
        text.textContent = "Dark Mode";
        text.style.fontSize = "13px";
        text.style.fontWeight = "500";

        const track = document.createElement("div");
        track.style.background = "#b0b3b8";
        track.style.width = "36px";
        track.style.height = "18px";
        track.style.borderRadius = "12px";
        track.style.position = "relative";
        track.style.transition = "all 0.25s ease";

        const knob = document.createElement("div");
        knob.style.width = "14px";
        knob.style.height = "14px";
        knob.style.background = "#ffffff";
        knob.style.borderRadius = "50%";
        knob.style.position = "absolute";
        knob.style.top = "2px";
        knob.style.transition = "all 0.25s cubic-bezier(0.4,0,0.2,1)";
        knob.style.boxShadow = "0 2px 6px rgba(0,0,0,0.4)";

        track.appendChild(knob);
        wrapper.appendChild(text);
        wrapper.appendChild(track);
        themeBtn.appendChild(wrapper);

        window._tvThemeToggle = { track, knob, text };

        // ⬅️ FIX: Apply saved theme immediately when button is created
        // (the useEffect fires before this button exists, so we must sync here too)
        const savedDark = localStorage.getItem("backtestx_theme") !== "light";
        if (savedDark) {
          track.style.background = "#2962ff";
          knob.style.left = "18px";
          text.style.color = "#d1d4dc";
        } else {
          track.style.background = "#b0b3b8";
          knob.style.left = "2px";
          text.style.color = "#131722";
        }

        themeBtn.addEventListener("click", toggleTheme);
      });

    });

    return () => {
      if (tvWidgetRef.current) {
        tvWidgetRef.current.remove();
        tvWidgetRef.current = null;
      }
    };
  }, []);
  // ==========================================
  // 🎨 SYNC TRADINGVIEW TOGGLE WITH REAL THEME
  // ==========================================
  // ==========================================
  // ==========================================
  // 🎨 FINAL STABLE SYNC (TV + HEADER TOGGLE)
  // ==========================================
  useEffect(() => {
    const applyThemeToTVToggle = () => {
      const tvToggle = window._tvThemeToggle;
      if (!tvToggle) return;

      const { track, knob, text } = tvToggle;

      if (isDarkMode) {
        // 🌙 DARK MODE (BLUE ON)
        track.style.background = "#2962ff";
        knob.style.left = "18px";
        text.style.color = "#d1d4dc";
      } else {
        // ☀️ LIGHT MODE (GREY OFF)
        track.style.background = "#b0b3b8";
        knob.style.left = "2px";
        text.style.color = "#131722";
      }
    };

    // Run immediately
    applyThemeToTVToggle();

    // 🔥 Critical: run again after TV header fully mounts
    const timeout = setTimeout(applyThemeToTVToggle, 100);

    return () => clearTimeout(timeout);
  }, [isDarkMode]);



  // ── Session restore ────────────────────────────────────────────────────────
  const restoreSession = async (session) => {
    setShowSessionPopup(false);
    try {
      if (session.balance != null) setBalance(session.balance);

      const symbol = session.symbol;
      const interval = tvWidgetRef.current?.symbolInterval()?.interval || '5';
      const symbolInfo = { name: symbol };

      // ⭐ Use the reliable restoreReplaySession method from getBars.js
      // It handles cache wiping, loading target+prev years, and finding exact index
      // even if Cache arrays were shifted due to previous year unshifting.
      const restoreTime = session.replayTime || 0;
      const restoreRes = await window.restoreReplaySession(symbolInfo, restoreTime);

      if (!restoreRes) {
        triggerAlert('Could not restore session: Missing cache data');
        return;
      }

      window._lastEmittedCandleTime = 0;
      replayModeTypeRef.current = session.mode || 'auto';
      currentSessionIdRef.current = session.id;

      // 📺 Set Symbol and safely jump to restored timestamp
      tvWidgetRef.current.setSymbol(symbol + '_REPLAY', interval, () => {
        setIsReplayUIVisible(true);
        const t = restoreTime / 1000;

        // Give TV 600ms to fetch the bars from getBars and parse them
        // before telling it to jump to exactly where we restored.
        setTimeout(() => {
          try {
            tvWidgetRef.current?.activeChart()?.setVisibleRange(
              { from: t - 60 * 60 * 24 * 3, to: t + 60 * 60 * 8 }
            );
          } catch (_) { }
        }, 600);
      });

      triggerAlert('✅ Session restored!');
    } catch (e) {
      console.error('Restore session error:', e);
      triggerAlert('Failed to restore session');
    }
  };

  const deleteSession = async (id) => {
    try {
      await SessionDB.delete(id);
      setSavedSessions(prev => prev.filter(s => s.id !== id));
    } catch (e) {
      console.warn('Delete session error:', e);
    }
  };

  // ── Mode choice – no confirmation popup, just start directly ─────────────
  const handleModeChoice = (mode) => {
    // ⭐ PREMIUM GATE: check active session count for free users
    if (!isPremium) {
      if (savedSessions.length >= FREE_SESSION_LIMIT) {
        setShowPremiumModal(true);
        return;
      }
    }
    handleModeChoiceInternal(mode);
  };

  const handleModeChoiceInternal = async (mode) => {
    setShowModeSelection(false);
    const chart = tvWidgetRef.current.activeChart();

    const range = chart.getVisibleRange();
    const midTime = Math.floor(range.from + (range.to - range.from) / 2);

    // Generate new session id for this run
    currentSessionIdRef.current = `session_${Date.now()}`;

    if (mode === 'manual') {

      // 🔥 MARK AS MANUAL (ANTI-CHEAT FLAG)
      replayModeTypeRef.current = 'manual';

      setIsSelecting(true);
      try {
        const id = await chart.createShape(
          { time: midTime },
          {
            shape: "vertical_line",
            lock: false,
            disableSelection: false,
            text: "DRAG TO START REPLAY",
            overrides: {
              linecolor: "#2962ff",
              linewidth: 4,
              showLabel: true,
              textcolor: "#ffffff",
              fontsize: 14,
              fillLabelBackground: true,
              labelBackgroundColor: "#2962ff",
            },
            zOrder: "top",
          }
        );

        replayLineIdRef.current = id;
        chart.setVisibleRange(range);

      } catch (e) {
        console.error("Failed to create manual selection line", e);
      }

    } else {

      replayModeTypeRef.current = 'auto';
      setIsPlaying(false);
      // ✅ RESET NEWS FOR AUTO REPLAY
      newsIndexRef.current = 0;

      const { symbol, interval } = tvWidgetRef.current.symbolInterval();

      const symbolInfo = {
        name: symbol.replace('_REPLAY', '')
      };
      window._lastEmittedCandleTime = 0;

      const auto = await window.startAutoReplay(symbolInfo);

      if (!auto) {
        triggerAlert("Auto replay failed");
        return;
      }

      tvWidgetRef.current.setSymbol(symbol + "_REPLAY", interval, () => {
        setIsReplayUIVisible(true);
        // ✅ RESET NEWS AGAIN AFTER SYMBOL SWITCH (IMPORTANT)
        newsIndexRef.current = 0;

        const chart = tvWidgetRef.current.activeChart();
        const t = auto.time / 1000;

        chart.setVisibleRange({
          from: t - (60 * 60 * 24 * 3),
          to: t + (60 * 60 * 8),
        });

        console.log("🎬 AUTO replay started at:", new Date(auto.time));

        // ✅ set replay time used by sync
        window.replayEnd = auto.time;

        // 💾 IMMEDIATE SAVE so the session is in IndexedDB right away
        // (the 30s auto-save fires too late if the user closes quickly)
        setTimeout(() => autoSaveSession(), 1000);
      });
    }
  };
  const confirmSelection = () => {
    const chart = tvWidgetRef.current.activeChart();
    const shape = chart.getShapeById(replayLineIdRef.current);

    if (!shape) {
      alert("Selection line not found. Please try again.");
      setIsSelecting(false);
      return;
    }

    const points = shape.getPoints();
    if (!points || points.length === 0) return;

    const selectedTime = points[0].time * 1000;
    // 🔥 USE REF HERE TOO
    const list = window.barCache["1"] || [];
    const targetIdx = list.findIndex(b => b.time >= selectedTime);

    if (targetIdx === -1) {
      alert("Selected point is outside loaded data.");
      return;
    }


    // 🔥 THIS IS ALSO MANUAL MODE
    replayModeTypeRef.current = 'manual';
    window.replayEnd = selectedTime;
    window._lastEmittedCandleTime = 0;
    window.replayIndex = targetIdx;
    window.replayMode = true;
    // 📰 FULLY CLEAR OLD NEWS ICONS FROM CHART (IMPORTANT)
    newsIndexRef.current = 0;


    chart.removeEntity(replayLineIdRef.current);
    replayLineIdRef.current = null;

    const { symbol, interval } = tvWidgetRef.current.symbolInterval();
    setIsPlaying(false);

    tvWidgetRef.current.setSymbol(symbol + "_REPLAY", interval, () => {
      // 📰 REMOVE OLD NEWS ICONS WHEN NEW REPLAY SYMBOL LOADS
      newsIndexRef.current = 0;
      setIsSelecting(false);
      setIsReplayUIVisible(true);
    });
  };

  const handlePlaceOrder = async () => {
    const chart = tvWidgetRef.current.activeChart();
    const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol || "XAUUSD";
    const activeSymbol = normalizeSymbol(rawSymbol);

    let uiPrice = currentReplayPrice;

    // ✅ SAFE LIMIT PRICE PARSING
    if (orderType === "limit") {
      const parsed = parseFloat(limitPrice);

      if (!limitPrice || isNaN(parsed) || parsed <= 0) {
        alert("⚠️ Enter a valid limit price");
        return;
      }

      uiPrice = parsed;

      // 🔥 REAL BROKER VALIDATION (CRITICAL)
      // BUY LIMIT must be BELOW current price
      if (side === 1 && uiPrice >= currentReplayPrice) {
        alert(
          `Invalid Buy Limit!\n\n` +
          `Current Price: ${currentReplayPrice.toFixed(2)}\n` +
          `Buy Limit must be BELOW market price.`
        );
        return;
      }

      // SELL LIMIT must be ABOVE current price
      if (side === -1 && uiPrice <= currentReplayPrice) {
        alert(
          `Invalid Sell Limit!\n\n` +
          `Current Price: ${currentReplayPrice.toFixed(2)}\n` +
          `Sell Limit must be ABOVE market price.`
        );
        return;
      }
    }
    // ⭐ SAFE COST DEFAULTS (ENGINE LEVEL)
    const safeSpread = spread === '' || spread == null ? 0 : Number(spread);
    const safeCommission = commission === '' || commission == null ? 0 : Number(commission);
    // 🔥 Apply spread like real broker
    const assetType = getAssetType(activeSymbol);

    // ⭐ BROKER REALISTIC ENTRY PRICE
    let realEntry = uiPrice;

    // Forex & Crypto → apply spread
    if (assetType === "forex" || assetType === "crypto") {
      realEntry = side === 1
        ? uiPrice + safeSpread
        : uiPrice - safeSpread; // SELL pays spread
    }
    // Stocks & Indices → NO spread (use commission instead)

    const qty = Number(lotSize);
    const lev = Number(leverage);

    // ✅ FIXED
    const lotValue = LOT_MULTIPLIER[activeSymbol] ?? 1;

    if (isNaN(uiPrice) || uiPrice <= 0) {
      alert("Invalid price.");
      return;
    }

    const requiredMarginRaw = (uiPrice * qty * lotValue) / lev;// <-- declare with const


    const requiredMargin = toDisplayAmount(requiredMarginRaw);

    if (balance < requiredMargin) {
      alert("Insufficient Balance!");
      return;
    }

    try {
      // ⭐ HTML OVERLAY handles line drawing — just compute prices
      let finalTpPrice = null;
      let finalSlPrice = null;

      if (orderType === "market") {
        finalTpPrice = tpInput
          ? parseFloat(tpInput)
          : (side === 1 ? uiPrice * 1.01 : uiPrice * 0.99);
        finalSlPrice = slInput
          ? parseFloat(slInput)
          : (side === 1 ? uiPrice * 0.99 : uiPrice * 1.01);
      }

      const newTrade = {
        id: "trade_" + Date.now(),
        side,
        symbol: normalizeSymbol(tvWidgetRef.current?.symbolInterval()?.symbol || "XAUUSD"),
        price: realEntry,
        qty,
        leverage: lev,
        orderType,
        isExecuted: orderType === "market",
        marginUsed: requiredMargin,
        _lockedMargin: requiredMargin,
        tpPrice: finalTpPrice,
        slPrice: finalSlPrice,
        pendingTP: tpInput,
        pendingSL: slInput,
        currency: selectedCurrency,
        openTime: Date.now()
      };

      // ✅ Deduct only if MARKET
      // 🔥 RESERVE MARGIN FOR BOTH MARKET + LIMIT (REALISTIC)
      const newBalance = balance - requiredMargin;

      // Safety check (double protection)
      if (newBalance < 0) {
        alert("Insufficient Balance!");
        return;
      }

      // Deduct margin immediately (like real exchanges)
      setBalance(newBalance);

      // 🔥 PROP CHALLENGE MODE 🔥
      const urlParams = new URLSearchParams(window.location.search);
      const propId = urlParams.get("prop");
      if (propId && window.WebBridge?.updatePropChallenge) {
        window.WebBridge.updatePropChallenge(propId, { balance: newBalance });
      } else if (window.AndroidBridge?.saveNewAmount) {
        window.AndroidBridge.saveNewAmount(newBalance);
      } else if (window.WebBridge?.saveNewAmount) {
        window.WebBridge.saveNewAmount(newBalance);
      }


      setOpenTrades(prev => [...prev, newTrade]);
      // 🔔 ORDER PLACED NOTIFICATION (PRO)
      if (orderType === "market") {
        showChartNotification(
          `${side === 1 ? "BUY" : "SELL"} Order Executed @ ${realEntry.toFixed(2)}`,
          "success"
        );
      } else {
        showChartNotification(
          `${side === 1 ? "BUY" : "SELL"} Limit Order Placed @ ${realEntry.toFixed(2)}`,
          "info"
        );
      }
      setTpInput("");
      setSlInput("");
      setShowOrderModal(false);
      setLimitPrice("");

      // 📋 SHOW TRADE CHECKLIST POPUP (if checklist exists)
      const bridge = window.AndroidBridge || window.WebBridge;
      if (bridge && bridge.getChecklist) {
        try {
          const items = await bridge.getChecklist();
          if (items && Array.isArray(items) && items.length > 0) {
            setTradeChecklistSelections(items.map(text => ({ text, ticked: false })));
            setPendingTradeForChecklist(newTrade); // store full trade object
            setShowTradeChecklistPopup(true);
          }
        } catch (e) {
          console.warn("Could not load checklist for trade popup:", e);
        }
      }

    } catch (err) {
      console.error("❌ Placement failed:", err);
    }
  };

  // 📋 SAVE CHECKLIST SELECTIONS WITH TRADE DATA → user6 via saveTradeToHistory
  const handleSaveTradeChecklist = async () => {
    const tickedItems = tradeChecklistSelections
      .filter(item => item.ticked)
      .map(item => item.text);

    // Only call Firebase if at least one item was ticked
    if (tickedItems.length > 0 && pendingTradeForChecklist) {
      const bridge = window.AndroidBridge || window.WebBridge;
      if (bridge && bridge.saveTradeToHistory) {
        const t = pendingTradeForChecklist; // full trade object stored at order time
        const rawSymbol = t.symbol || (window.currentChartSymbol || "Unknown");
        const tradeRecord = {
          symbol: rawSymbol,
          type: t.side === 1 ? "BUY" : "SELL",
          status: t.orderType === "limit" ? "PENDING" : "OPEN",
          pnl: 0,
          entry: t.price || 0,
          exit: 0,
          lots: t.qty || 0,
          leverage: `${t.leverage || 1}x`,
          marginUsed: t.marginUsed || 0,
          sl: t.slPrice ?? null,
          tp: t.tpPrice ?? null,
          checklistItems: tickedItems   // ← stored in user6 trade document
        };
        try {
          await bridge.saveTradeToHistory(JSON.stringify(tradeRecord));
          console.log("📋 Checklist saved to user6 with trade:", tickedItems);
        } catch (e) {
          console.error("saveTradeToHistory (checklist) error:", e);
        }
      }
    }

    setShowTradeChecklistPopup(false);
    setTradeChecklistSelections([]);
    setPendingTradeForChecklist(null);
  };

  const toggleTradeChecklistItem = (index) => {
    setTradeChecklistSelections(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ticked: !updated[index].ticked };
      return updated;
    });
  };

  const toggleSpeed = () => {
    const speeds = [3000, 2000, 1000, 500, 200, 100, 50, 25, 10, 5];
    const currentIndex = speeds.indexOf(replaySpeed);
    setReplaySpeed(speeds[(currentIndex + 1) % speeds.length]);
  };
  const themeColors = {
    bg: isDarkMode ? '#131722' : '#ffffff',
    header: isDarkMode ? '#1e222d' : '#f0f3fa',
    text: isDarkMode ? '#d1d4dc' : '#131722',
    border: isDarkMode ? '#434651' : '#d1d4dc',
    muted: isDarkMode ? '#868993' : '#5d606b'
  };
  // ===============================
  // 📰 SMART NEWS FILTER (PRO LOGIC - NO LAG)
  // ===============================
  // 🔥 Use a ref to store the last update time so we don't spam setFilteredNews
  const newsFilterLastUpdateRef = useRef(0);
  const newsFilterRafRef = useRef(null);

  // Helper: compute filtered news from a given replay time (ms)
  const computeFilteredNews = (replayTime) => {
    const news = window.newsEvents || [];
    if (!news.length) return [];

    if (newsMode === "auto") {
      return news.filter(n => n.time >= replayTime).slice(0, 10);
    } else {
      if (manualFilter === "upcoming") {
        return news.filter(n => n.time >= replayTime).slice(0, 20);
      } else if (manualFilter === "today") {
        const replayDate = new Date(replayTime).toDateString();
        return news.filter(n => new Date(n.time).toDateString() === replayDate);
      } else if (manualFilter === "high") {
        return news.filter(n => n.time >= replayTime && n.impact === 3).slice(0, 15);
      }
    }
    return [];
  };

  useEffect(() => {
    if (!showNewsPanel) return;

    // 🔥 HELPER: get the current replay time anchor
    const getReplayTime = () =>
      window.replayEnd ||
      (window.barCache?.["1"]?.[Math.max(0, window.replayIndex)]?.time) ||
      null;

    // 🔥 Immediately try to populate (may be empty if news still loading)
    const initialTime = getReplayTime();
    if (initialTime) {
      setFilteredNews(computeFilteredNews(initialTime));
    }
    newsFilterLastUpdateRef.current = Date.now();

    // 🔥 FAST RETRY SCHEDULE: check quickly for the first 3 seconds in case
    // news is still being loaded (Android bridge is async)
    // After 3s, fall back to slow 1/sec polling
    let rafId;
    const panelOpenTime = Date.now();
    const FAST_PHASE_MS = 3000;  // first 3 seconds: update every 200ms
    const FAST_INTERVAL = 200;
    const SLOW_INTERVAL = 1000;

    const tick = () => {
      const now = Date.now();
      const elapsed = now - panelOpenTime;
      const updateInterval = elapsed < FAST_PHASE_MS ? FAST_INTERVAL : SLOW_INTERVAL;

      if (now - newsFilterLastUpdateRef.current >= updateInterval) {
        try {
          const replayTime = getReplayTime();
          if (!replayTime) {
            newsFilterLastUpdateRef.current = now;
            rafId = requestAnimationFrame(tick);
            return;
          }

          const result = computeFilteredNews(replayTime);

          // 🔥 Only call setFilteredNews if result actually changed
          // (avoids pointless React re-renders during slow polling)
          setFilteredNews(prev => {
            if (prev.length !== result.length) return result;
            // Quick deep-check on first item only (titles are unique enough)
            if (prev[0]?.time !== result[0]?.time || prev[prev.length - 1]?.time !== result[result.length - 1]?.time) return result;
            return prev; // no change — skip re-render
          });

          newsFilterLastUpdateRef.current = now;
        } catch (e) {
          console.error("News filter error:", e);
        }
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    newsFilterRafRef.current = rafId;

    return () => {
      cancelAnimationFrame(rafId);
      newsFilterRafRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewsPanel, newsMode, manualFilter]);

  // ======================================
  // 🌍 FORMAT NEWS TIME BASED ON USER UTC
  // ======================================
  const formatNewsTimeByChartTZ = (timestamp) => {
    if (!timestamp || !tvWidgetRef.current) return "";

    const chart = tvWidgetRef.current.chart();
    const tz = chart.getTimezone(); // 🔥 TradingView timezone

    const date = new Date(timestamp);

    return date.toLocaleString("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }) + ` (${tz})`;
  };

  const sendTradeToAndroid = (trade, exitPrice, status) => {
    if (window.AndroidBridge && window.AndroidBridge.onTradeClosed) {
      const pnl = (exitPrice - trade.price) * trade.side * trade.qty;

      const tradeData = {
        symbol: "XAUUSD",
        type: trade.side === 1 ? "BUY" : "SELL",
        status: status,
        pnl: pnl,
        entry: trade.price,
        exit: exitPrice,
        lots: trade.qty,
        leverage: `${trade.leverage}x`,
        sl: trade.tvLines.slLine.getPrice(),
        tp: trade.tvLines.tpLine.getPrice()
      };

      window.AndroidBridge.onTradeClosed(JSON.stringify(tradeData));
    }
  };
  const getTradeTime = () => {
    const now = new Date();
    return now.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }); // example: "22:00"
  };
  // ===== TradingView Native Loader Control =====
  let _tvLoaderStart = 0;

  const showTVLoader = () => {
    _tvLoaderStart = Date.now();
    window.tvWidgetRef?.current?.showLoadingScreen?.();
  };

  const hideTVLoader = () => {
    const elapsed = Date.now() - _tvLoaderStart;
    const wait = Math.max(0, 3000 - elapsed); // fixed 3 sec

    setTimeout(() => {
      window.tvWidgetRef?.current?.hideLoadingScreen?.();
    }, wait);
  };
  // ⭐ CURRENT ASSET TYPE FOR UI (SPREAD vs COMMISSION)
  let uiAssetType = "forex";
  try {
    const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol;
    const activeSymbol = normalizeSymbol(rawSymbol);
    uiAssetType = getAssetType(activeSymbol);
  } catch (e) {
    uiAssetType = "forex"; // safe fallback
  }

  // 📝 CHECKLIST ACTIONS
  useEffect(() => {
    // Note: If checklistItems is empty, try to fetch it once when the panel opens.
    // If the user hasn't created one, it'll just stay empty.
    if (showChecklistPanel && checklistItems.length === 0 && !isEditingChecklist) {
      const bridge = window.AndroidBridge || window.WebBridge;
      if (bridge && bridge.getChecklist) {
        bridge.getChecklist().then(items => {
          if (items && Array.isArray(items) && items.length > 0) {
            setChecklistItems(items.map(text => ({ text, checked: false })));
          }
        });
      }
    }
  }, [showChecklistPanel]); // Remove isEditingChecklist and checklistItems from deps to strictly run when opening panel

  const toggleChecklistItem = (index) => {
    const newItems = [...checklistItems];
    newItems[index].checked = !newItems[index].checked;
    setChecklistItems(newItems);
  };

  const addChecklistItem = () => {
    if (newChecklistItem.trim() === "") return;
    setChecklistItems([...checklistItems, { text: newChecklistItem, checked: false }]);
    setNewChecklistItem("");
  };

  const removeChecklistItem = (index) => {
    setChecklistItems(checklistItems.filter((_, i) => i !== index));
  };

  const handleSaveChecklist = async () => {
    const bridge = window.AndroidBridge || window.WebBridge;
    if (bridge && bridge.saveChecklist) {
      const itemsToSave = checklistItems.map(item => item.text);
      await bridge.saveChecklist(itemsToSave);
      setIsEditingChecklist(false);
    }
  };

  // 🏆 PROP CHALLENGE EQUITY TRACKING 🏆
  const [floatingPnL, setFloatingPnL] = useState(0);
  const [equity, setEquity] = useState(balance);

  useEffect(() => {
    if (!activePropChallenge) return;
    
    let floatPnL = 0;
    openTrades.forEach(trade => {
      if (trade.isExecuted) {
        const rawSymbol = window.currentChartSymbol || trade.symbol || "XAUUSD";
        const normalized = normalizeSymbol(rawSymbol);
        const lotValue = LOT_MULTIPLIER[normalized] || 1;
        
        let pnl = (currentReplayPrice - trade.price) * trade.side * trade.qty * lotValue;
        
        // Commission deduction
        const assetType = getAssetType(normalized);
        if (assetType === "stock" || assetType === "index") {
          const tradeValue = trade.price * trade.qty * lotValue;
          const commCost = (tradeValue * (commission === '' ? 0.2 : commission)) / 100;
          pnl -= commCost;
        }
        floatPnL += pnl;
      }
    });

    const currentEquity = balance + floatPnL;
    setFloatingPnL(floatPnL);
    setEquity(currentEquity);

    // 🔥 PROP FIRM RULE EVALUATION 🔥
    // Only check rules if challenge is active (not already failed or passed)
    if (activePropChallenge.status === "Phase 1" || activePropChallenge.status === "Phase 2") {
      const targetPct = parseFloat(activePropChallenge.target || 8) / 100;
      const dlPct = parseFloat(activePropChallenge.dailyLossLimit || 5) / 100;
      const mlPct = parseFloat(activePropChallenge.maxLossLimit || 10) / 100;
      
      const targetBal = propStartBalance * (1 + targetPct);
      const dailyLossThreshold = propStartBalance * (1 - dlPct);
      const maxLossThreshold = propStartBalance * (1 - mlPct);

      if (currentEquity <= maxLossThreshold) {
         window.WebBridge?.updatePropChallenge(activePropChallenge.id, { status: "Failed (Max Loss)", progress: 0 });
         alert("🚨 Max Loss Limit Breached! Challenge Failed.");
         setIsPlaying(false);
      } else if (currentEquity <= dailyLossThreshold) {
         window.WebBridge?.updatePropChallenge(activePropChallenge.id, { status: "Failed (Daily Loss)", progress: 0 });
         alert("🚨 Daily Loss Limit Breached! Challenge Failed.");
         setIsPlaying(false);
      } else if (currentEquity >= targetBal) {
         window.WebBridge?.updatePropChallenge(activePropChallenge.id, { status: "Passed", progress: 100 });
         alert("🎉 Profit Target Reached! You passed this phase.");
         setIsPlaying(false);
      }
    }
  }, [currentReplayPrice, openTrades, balance, activePropChallenge, propStartBalance, commission]);

  return (
    <div style={{ ...mainContainerStyle, backgroundColor: themeColors.bg }}>
      {/* 1. TOP HEADER AREA (REPLAY CONTROLS OR MANUAL SELECTION) */}
      {(isReplayUIVisible || isSelecting) && (
        <div
          style={{
            ...headerAreaStyle,
            backgroundColor: themeColors.header,
            borderBottom: `1px solid ${themeColors.border}`,
            justifyContent: 'center', // keep center alignment
            gap: '20px'
          }}
        >
          {isReplayUIVisible ? (
            <>
              {/* 📰 NEWS BUTTON (LEFT SIDE) */}
              <button
                onClick={() => setShowNewsPanel(true)}
                style={{
                  background: "transparent",
                  border: `1px solid ${themeColors.border}`,
                  color: "#ffc107",
                  fontWeight: "bold",
                  fontSize: "13px",
                  cursor: "pointer",
                  padding: "6px 14px",
                  borderRadius: "6px",
                  transition: "0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? "#2a2e39" : "#e0e3eb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                📰 News
              </button>

              {/* ✅ CHECKLIST BUTTON */}
              <button
                onClick={() => setShowChecklistPanel(true)}
                style={{
                  background: "transparent",
                  border: `1px solid ${themeColors.border}`,
                  color: "#089981",
                  fontWeight: "bold",
                  fontSize: "13px",
                  cursor: "pointer",
                  padding: "6px 14px",
                  borderRadius: "6px",
                  transition: "0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? "#2a2e39" : "#e0e3eb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                ✅ Checklist
              </button>
              {/* 🔴 EXIT REPLAY BUTTON (CENTER) */}
              <button
                style={{
                  background: "none",
                  border: "none",
                  color: "#f23645",
                  fontWeight: "bold",
                  fontSize: "14px",
                  cursor: "pointer",
                  padding: "6px 14px",
                  borderRadius: "6px",
                  transition: "0.2s",
                }}
                onClick={() => setShowExitConfirm(true)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? "#2a2e39" : "#e0e3eb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                Exit Replay
              </button>
            </>
          ) : (
            /* 🟦 KEEP MANUAL MODE UI (DO NOT REMOVE) */
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <span style={{ ...labelStyle, color: themeColors.text }}>
                Drag line to start point
              </span>

              <button style={confirmBtnStyle} onClick={confirmSelection}>
                Confirm ✓
              </button>

              <button
                style={{
                  ...btnStyle,
                  color: '#f23645',
                  border: '1px solid #f23645',
                  padding: '4px 10px',
                  borderRadius: '4px'
                }}
                onClick={() => {
                  const chart = tvWidgetRef.current.activeChart();
                  chart.removeEntity(replayLineIdRef.current);
                  setIsSelecting(false);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{ ...chartWrapperStyle, position: 'relative' }}>
        <div id="tv_chart_container" ref={chartContainerRef} style={{ height: "100%", width: "100%" }} />
        
        {/* 🏆 PROP FIRM CHALLENGE HUD */}
        {activePropChallenge && isReplayUIVisible && (
          <div
            style={{
              position: "absolute",
              top: "20px",
              left: "20px",
              zIndex: 50,
              backgroundColor: isDarkMode ? "rgba(30, 34, 45, 0.85)" : "rgba(255, 255, 255, 0.85)",
              backdropFilter: "blur(4px)",
              padding: "15px",
              borderRadius: "8px",
              border: `1px solid ${isDarkMode ? "#434651" : "#d1d4dc"}`,
              color: isDarkMode ? "#d1d4dc" : "#131722",
              minWidth: "250px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              pointerEvents: "none"
            }}
          >
            <h3 style={{ margin: "0 0 10px 0", fontSize: "14px", borderBottom: `1px solid ${isDarkMode ? "#434651" : "#e0e3eb"}`, paddingBottom: "5px" }}>
              🏆 {activePropChallenge.firmName} - {activePropChallenge.status}
            </h3>
            
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
              <span style={{ fontSize: "12px", color: isDarkMode ? "#868993" : "#5d606b" }}>Current Equity:</span>
              <span style={{ fontSize: "13px", fontWeight: "bold" }}>${equity.toFixed(2)}</span>
            </div>
            
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
              <span style={{ fontSize: "12px", color: isDarkMode ? "#868993" : "#5d606b" }}>Floating PnL:</span>
              <span style={{ fontSize: "13px", fontWeight: "bold", color: floatingPnL >= 0 ? "#089981" : "#f23645" }}>
                {floatingPnL >= 0 ? "+" : ""}${floatingPnL.toFixed(2)}
              </span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
              <span style={{ fontSize: "12px", color: isDarkMode ? "#868993" : "#5d606b" }}>Target ({activePropChallenge.target}):</span>
              <span style={{ fontSize: "12px", color: "#2962ff" }}>${(propStartBalance * (1 + parseFloat(activePropChallenge.target)/100)).toFixed(2)}</span>
            </div>
            
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
              <span style={{ fontSize: "12px", color: isDarkMode ? "#868993" : "#5d606b" }}>Max Loss ({activePropChallenge.maxLossLimit}):</span>
              <span style={{ fontSize: "12px", color: "#f23645" }}>${(propStartBalance * (1 - parseFloat(activePropChallenge.maxLossLimit)/100)).toFixed(2)}</span>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: "12px", color: isDarkMode ? "#868993" : "#5d606b" }}>Daily Loss ({activePropChallenge.dailyLossLimit}):</span>
              <span style={{ fontSize: "12px", color: "#ff9800" }}>${(propStartBalance * (1 - parseFloat(activePropChallenge.dailyLossLimit)/100)).toFixed(2)}</span>
            </div>
          </div>
        )}
        
        <HtmlOrderOverlay
          openTrades={openTrades}
          setOpenTrades={setOpenTrades}
          tvWidgetRef={tvWidgetRef}
          currSymbol={currSymbol}
          currRate={currRate}
          toDisplayAmount={toDisplayAmount}
          LOT_MULTIPLIER={LOT_MULTIPLIER}
        />
        {/* 🔔 PROFESSIONAL CHART NOTIFICATIONS (TOP RIGHT) */}
        {/* 🔔 PROFESSIONAL CHART NOTIFICATIONS (TOP RIGHT) */}
        {chartNotifications.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 99999,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              pointerEvents: "none"
            }}
          >
            {chartNotifications.map((notif) => (
              <div
                key={notif.id}
                style={{
                  background: notif.color,
                  color: "#fff",
                  padding: "10px 14px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "bold",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.45)",
                  animation: "fadeSlide 0.3s ease",
                  minWidth: "220px"
                }}
              >
                {notif.text}
              </div>
            ))}
          </div>
        )}
        {/* 📰 REPLAY NEWS PANEL (RIGHT SIDE) */}
        {isReplayUIVisible && showNewsPanel && (
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "360px",
              height: "100%",
              background: isDarkMode ? "#0e1116" : "#ffffff",
              borderLeft: `1px solid ${themeColors.border}`,
              zIndex: 99999,
              display: "flex",
              flexDirection: "column",
              boxShadow: "-8px 0 25px rgba(0,0,0,0.45)",
            }}
          >
            {/* HEADER */}
            <div
              style={{
                padding: "14px",
                borderBottom: `1px solid ${themeColors.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: isDarkMode ? "#131722" : "#f0f3fa"
              }}
            >
              <span style={{ fontWeight: "bold", color: themeColors.text }}>
                📰 Replay News
              </span>

              <button
                onClick={() => setShowNewsPanel(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: themeColors.text,
                  fontSize: "18px",
                  cursor: "pointer"
                }}
              >
                ✕
              </button>
            </div>

            {/* NEWS LIST */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "12px"
              }}
            >
              {/* 🧠 MODE SWITCH (AUTO / MANUAL) */}
              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <button
                  onClick={() => setNewsMode("auto")}
                  style={{
                    flex: 1,
                    padding: "6px",
                    borderRadius: "6px",
                    border: "none",
                    background: newsMode === "auto" ? "#2962ff" : "#2a2e39",
                    color: "white",
                    fontSize: "12px",
                    cursor: "pointer"
                  }}
                >
                  AUTO
                </button>

                <button
                  onClick={() => setNewsMode("manual")}
                  style={{
                    flex: 1,
                    padding: "6px",
                    borderRadius: "6px",
                    border: "none",
                    background: newsMode === "manual" ? "#2962ff" : "#2a2e39",
                    color: "white",
                    fontSize: "12px",
                    cursor: "pointer"
                  }}
                >
                  MANUAL
                </button>
              </div>

              {/* 🎯 MANUAL FILTER OPTIONS */}
              {newsMode === "manual" && (
                <div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
                  {["upcoming", "today", "high"].map((type) => (
                    <button
                      key={type}
                      onClick={() => setManualFilter(type)}
                      style={{
                        flex: 1,
                        padding: "5px",
                        borderRadius: "6px",
                        border: "1px solid #434651",
                        background: manualFilter === type ? "#ffc10722" : "transparent",
                        color: themeColors.text,
                        fontSize: "11px",
                        cursor: "pointer",
                        fontWeight: "bold"
                      }}
                    >
                      {type.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}

              {/* 📰 FILTERED NEWS LIST (PRO) */}
              {filteredNews.length === 0 ? (
                <div style={{ color: themeColors.muted, fontSize: "13px" }}>
                  No upcoming news for this replay time
                </div>
              ) : (
                filteredNews.map((news, index) => {

                  const impactColor =
                    news.impact === 3 ? "#ff5252" :
                      news.impact === 2 ? "#ffc107" : "#2196f3";

                  return (
                    <div
                      key={index}
                      style={{
                        padding: "12px",
                        marginBottom: "10px",
                        borderRadius: "10px",
                        background: isDarkMode ? "#1e222d" : "#f8f9fb",
                        borderLeft: `4px solid ${impactColor}`
                      }}
                    >
                      <div style={{
                        fontSize: "13px",
                        fontWeight: "bold",
                        color: themeColors.text
                      }}>
                        {news.title}
                      </div>

                      <div style={{
                        fontSize: "11px",
                        color: themeColors.muted,
                        marginTop: "4px"
                      }}>
                        {formatNewsTimeByChartTZ(news.time)}
                      </div>

                      <div style={{
                        fontSize: "11px",
                        marginTop: "6px",
                        color: impactColor,
                        fontWeight: "bold"
                      }}>
                        {news.impact === 3 ? "HIGH IMPACT 🔴" :
                          news.impact === 2 ? "MEDIUM IMPACT 🟡" :
                            "LOW IMPACT 🔵"}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ✅ CHECKLIST PANEL */}
        {isReplayUIVisible && showChecklistPanel && (
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "360px",
              height: "100%",
              background: isDarkMode ? "#0e1116" : "#ffffff",
              borderLeft: `1px solid ${themeColors.border}`,
              zIndex: 99999,
              display: "flex",
              flexDirection: "column",
              boxShadow: "-8px 0 25px rgba(0,0,0,0.45)",
            }}
          >
            {/* HEADER */}
            <div
              style={{
                padding: "14px",
                borderBottom: `1px solid ${themeColors.border}`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: isDarkMode ? "#131722" : "#f0f3fa"
              }}
            >
              <span style={{ fontWeight: "bold", color: themeColors.text }}>
                ✅ Trading Checklist
              </span>

              <button
                onClick={() => setShowChecklistPanel(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: themeColors.text,
                  fontSize: "18px",
                  cursor: "pointer"
                }}
              >
                ✕
              </button>
            </div>

            {/* CONTENT */}
            <div style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
              {isEditingChecklist ? (
                <>
                  <div style={{ marginBottom: "16px", color: themeColors.text, fontWeight: "bold" }}>
                    Edit Checklist Items
                  </div>
                  {checklistItems.map((item, index) => (
                    <div key={index} style={{ display: "flex", alignItems: "center", marginBottom: "8px", gap: "8px" }}>
                      <div style={{ flex: 1, color: themeColors.text, padding: "8px", background: isDarkMode ? "#1e222d" : "#f8f9fb", borderRadius: "6px" }}>
                        {item.text}
                      </div>
                      <button
                        onClick={() => removeChecklistItem(index)}
                        style={{
                          background: "#f23645", color: "white", border: "none", borderRadius: "6px",
                          width: "30px", height: "30px", cursor: "pointer", fontWeight: "bold"
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                    <input
                      type="text"
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      placeholder="e.g. Trend is bullish"
                      onKeyDown={(e) => { if (e.key === "Enter") addChecklistItem(); }}
                      style={{
                        flex: 1, padding: "8px", borderRadius: "6px", border: `1px solid ${themeColors.border}`,
                        background: isDarkMode ? "#131722" : "#ffffff", color: themeColors.text,
                        outline: "none"
                      }}
                    />
                    <button
                      onClick={addChecklistItem}
                      style={{
                        background: "#2962ff", color: "white", border: "none", padding: "8px 12px",
                        borderRadius: "6px", cursor: "pointer", fontWeight: "bold"
                      }}
                    >
                      Add
                    </button>
                  </div>
                  <button
                    onClick={handleSaveChecklist}
                    style={{
                      width: "100%", marginTop: "20px", padding: "12px", background: "#089981",
                      color: "white", border: "none", borderRadius: "6px", fontWeight: "bold", cursor: "pointer"
                    }}
                  >
                    Save Checklist
                  </button>
                </>
              ) : (
                <>
                  {checklistItems.length === 0 ? (
                    <div style={{ textAlign: "center", marginTop: "40px" }}>
                      <div style={{ color: themeColors.muted, marginBottom: "20px" }}>
                        No checklist items found.
                      </div>
                      <button
                        onClick={() => setIsEditingChecklist(true)}
                        style={{
                          background: "#2962ff", color: "white", border: "none", padding: "10px 20px",
                          borderRadius: "6px", cursor: "pointer", fontWeight: "bold"
                        }}
                      >
                        Create Checklist
                      </button>
                    </div>
                  ) : (
                    <>
                      <div style={{ marginBottom: "16px", color: themeColors.text, fontSize: "14px", opacity: 0.8 }}>
                        Ensure conditions are met before trading.
                      </div>
                      {checklistItems.map((item, index) => (
                        <div
                          key={index}
                          onClick={() => toggleChecklistItem(index)}
                          style={{
                            display: "flex", alignItems: "center", padding: "12px", marginBottom: "8px",
                            background: isDarkMode ? "#1e222d" : "#f8f9fb", borderRadius: "8px",
                            cursor: "pointer", border: `1px solid ${item.checked ? "#089981" : "transparent"}`,
                            transition: "0.2s"
                          }}
                        >
                          <div style={{
                            width: "20px", height: "20px", borderRadius: "4px", border: `2px solid ${item.checked ? "#089981" : themeColors.border}`,
                            background: item.checked ? "#089981" : "transparent", display: "flex",
                            alignItems: "center", justifyContent: "center", marginRight: "12px",
                            transition: "0.2s"
                          }}>
                            {item.checked && <span style={{ color: "white", fontSize: "12px", fontWeight: "bold" }}>✓</span>}
                          </div>
                          <span style={{
                            color: themeColors.text,
                            textDecoration: item.checked ? "line-through" : "none",
                            opacity: item.checked ? 0.6 : 1,
                            flex: 1,
                            transition: "0.2s"
                          }}>
                            {item.text}
                          </span>
                        </div>
                      ))}
                      <button
                        onClick={() => setIsEditingChecklist(true)}
                        style={{
                          width: "100%", marginTop: "20px", padding: "10px", background: "transparent",
                          color: themeColors.text, border: `1px dashed ${themeColors.border}`,
                          borderRadius: "6px", cursor: "pointer", fontWeight: "bold",
                          opacity: 0.8
                        }}
                      >
                        Edit Checklist
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* 📋 TRADE CHECKLIST POPUP – appears after placing a trade */}
        {showTradeChecklistPopup && (
          <div style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.6)",
            zIndex: 999999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)"
          }}>
            <div style={{
              background: isDarkMode ? "#1a1e2d" : "#ffffff",
              borderRadius: "16px",
              width: "360px",
              maxWidth: "95vw",
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
              border: `1px solid ${isDarkMode ? "#2a2e45" : "#e0e3eb"}`,
              overflow: "hidden"
            }}>
              {/* HEADER */}
              <div style={{
                padding: "18px 20px 14px",
                borderBottom: `1px solid ${isDarkMode ? "#2a2e45" : "#e0e3eb"}`,
                background: isDarkMode ? "#131722" : "#f8f9fb"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
                  <span style={{ fontSize: "20px" }}>📋</span>
                  <span style={{
                    fontWeight: "700",
                    fontSize: "15px",
                    color: themeColors.text,
                    letterSpacing: "0.3px"
                  }}>Trade Checklist</span>
                </div>
                <div style={{ fontSize: "12px", color: themeColors.muted, paddingLeft: "30px" }}>
                  Tick items you followed for this trade
                </div>
              </div>

              {/* CHECKLIST ITEMS */}
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
                {tradeChecklistSelections.length === 0 ? (
                  <div style={{ color: themeColors.muted, textAlign: "center", padding: "30px 0" }}>No items</div>
                ) : (
                  tradeChecklistSelections.map((item, idx) => (
                    <div
                      key={idx}
                      onClick={() => toggleTradeChecklistItem(idx)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        padding: "13px 14px",
                        marginBottom: "8px",
                        borderRadius: "10px",
                        cursor: "pointer",
                        background: item.ticked
                          ? (isDarkMode ? "rgba(8,153,129,0.12)" : "rgba(8,153,129,0.08)")
                          : (isDarkMode ? "#1e222d" : "#f8f9fb"),
                        border: `1.5px solid ${item.ticked ? "#089981" : (isDarkMode ? "#2a2e45" : "#e0e3eb")
                          }`,
                        transition: "all 0.18s ease",
                        userSelect: "none"
                      }}
                    >
                      {/* Custom Checkbox */}
                      <div style={{
                        width: "22px",
                        height: "22px",
                        borderRadius: "6px",
                        border: `2px solid ${item.ticked ? "#089981" : (isDarkMode ? "#434651" : "#c0c3cc")}`,
                        background: item.ticked ? "#089981" : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        transition: "all 0.18s ease"
                      }}>
                        {item.ticked && (
                          <svg width="13" height="10" viewBox="0 0 13 10" fill="none">
                            <path d="M1.5 5L5 8.5L11.5 1.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <span style={{
                        color: themeColors.text,
                        fontSize: "14px",
                        fontWeight: item.ticked ? "600" : "400",
                        textDecoration: item.ticked ? "none" : "none",
                        flex: 1,
                        lineHeight: "1.4",
                        transition: "all 0.18s ease"
                      }}>
                        {item.text}
                      </span>
                      {item.ticked && (
                        <span style={{ color: "#089981", fontSize: "16px", flexShrink: 0 }}>✓</span>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* FOOTER BUTTONS */}
              <div style={{
                padding: "14px 20px",
                borderTop: `1px solid ${isDarkMode ? "#2a2e45" : "#e0e3eb"}`,
                display: "flex",
                gap: "10px",
                background: isDarkMode ? "#131722" : "#f8f9fb"
              }}>
                {/* Skip button */}
                <button
                  onClick={() => {
                    setShowTradeChecklistPopup(false);
                    setTradeChecklistSelections([]);
                    setPendingTradeForChecklist(null);
                  }}
                  style={{
                    flex: 1,
                    padding: "11px",
                    borderRadius: "9px",
                    border: `1px solid ${isDarkMode ? "#434651" : "#d1d4dc"}`,
                    background: "transparent",
                    color: themeColors.muted,
                    fontWeight: "600",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "0.2s"
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = isDarkMode ? "#2a2e39" : "#e8eaf0"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  Skip
                </button>
                {/* Save button */}
                <button
                  onClick={handleSaveTradeChecklist}
                  style={{
                    flex: 2,
                    padding: "11px",
                    borderRadius: "9px",
                    border: "none",
                    background: tradeChecklistSelections.some(i => i.ticked)
                      ? "linear-gradient(135deg, #089981, #06837e)"
                      : (isDarkMode ? "#2a2e39" : "#d1d4dc"),
                    color: tradeChecklistSelections.some(i => i.ticked) ? "white" : themeColors.muted,
                    fontWeight: "700",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "0.2s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px"
                  }}
                >
                  <span>💾</span>
                  Save ({tradeChecklistSelections.filter(i => i.ticked).length}/{tradeChecklistSelections.length})
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🎮 FLOATING REPLAY CONTROLS (DRAG HANDLE) */}
        {isReplayUIVisible && (
          <div
            ref={floatingControlsRef}
            style={{
              position: "absolute",
              top: "80px",
              left: "20px",
              zIndex: 9999,
              pointerEvents: "auto",
              userSelect: "none", // prevents text selection while dragging
              background: isDarkMode ? "#1e222d" : "#ffffff",
              border: `1px solid ${themeColors.border}`,
              borderRadius: "12px",
              padding: "8px 12px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.45)",
            }}
          >
            {/* 🔥 DRAG HANDLE (DOTS) */}
            <div
              ref={dragHandleRef}
              style={{
                cursor: "grab",
                padding: "6px 8px",
                borderRadius: "8px",
                background: isDarkMode ? "#2a2e39" : "#f0f3fa",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
                userSelect: "none",
                touchAction: "none" // ⭐ REQUIRED for mobile drag
              }}
              title="Drag"
            >
              ⠿
            </div>


            {/* SPEED SLIDER */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span style={{ fontSize: "10px", color: themeColors.muted }}>
                {speedLabels[replaySpeed]}
              </span>
              <input
                type="range"
                min="0"
                max={speeds.length - 1}
                step="1"
                value={currentSpeedIndex}
                onChange={handleSliderChange}
                style={{ width: "90px", cursor: "pointer" }}
              />
            </div>
            {/* STEP TF (TradingView Style Dropdown) */}
            <div style={{ position: "relative" }} className="step-dropdown">
              {/* Button */}
              <div
                onClick={() => setShowStepDropdown(prev => !prev)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "8px",
                  background: isDarkMode ? "#2a2e39" : "#f0f3fa",
                  color: themeColors.text,
                  border: `1px solid ${themeColors.border}`,
                  fontSize: "12px",
                  cursor: "pointer",
                  minWidth: "55px",
                  textAlign: "center",
                  fontWeight: "600",
                  userSelect: "none"
                }}
              >
                {replayStep === "60" ? "1hr" :
                  replayStep === "240" ? "4hr" :
                    `${replayStep}m`} ▾
              </div>

              {/* Dropdown Menu */}
              {showStepDropdown && (
                <div
                  style={{
                    position: "absolute",
                    top: "120%",
                    left: 0,
                    background: isDarkMode
                      ? "rgba(30,34,45,0.95)"
                      : "rgba(255,255,255,0.95)",
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: "10px",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.4)",
                    zIndex: 99999,
                    overflow: "hidden",
                    minWidth: "80px",
                    backdropFilter: "blur(8px)"
                  }}
                >
                  {["1", "2", "3", "5", "10", "15", "30", "60", "240"].map((step) => (
                    <div
                      key={step}
                      onClick={() => {
                        setReplayStep(step);
                        setShowStepDropdown(false);
                      }}
                      style={{
                        padding: "9px 12px",
                        fontSize: "12px",
                        cursor: "pointer",
                        background:
                          replayStep === step
                            ? (isDarkMode ? "#2962ff33" : "#2962ff22")
                            : "transparent",
                        color: themeColors.text,
                        fontWeight: replayStep === step ? "bold" : "500",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                      }}
                      onMouseEnter={(e) => {
                        if (replayStep !== step) {
                          e.currentTarget.style.background = isDarkMode
                            ? "#2a2e39"
                            : "#f0f3fa";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (replayStep !== step) {
                          e.currentTarget.style.background = "transparent";
                        }
                      }}
                    >
                      {step === "60" ? "1hr" :
                        step === "240" ? "4hr" :
                          `${step}m`}

                      {replayStep === step && (
                        <span style={{ color: "#2962ff", fontSize: "14px" }}>✓</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* PLAY */}
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              style={{
                padding: "6px 10px",
                borderRadius: "6px",
                border: "none",
                background: "#2962ff",
                color: "white",
                fontWeight: "bold",
                cursor: "pointer"
              }}
            >
              {isPlaying ? "⏸" : "▶"}
            </button>

            {/* STEP */}
            <button
              onClick={() => window.executeNextStep()}
              style={{
                padding: "6px 10px",
                borderRadius: "6px",
                border: "none",
                background: "#363a45",
                color: "white",
                fontWeight: "bold",
                cursor: "pointer"
              }}
            >
              ➜
            </button>
          </div>
        )}
        {/* 2. REPLAY MODE SELECTION MODAL */}
        {showModeSelection && (
          <div style={modalOverlay}>
            <div style={{
              background: isDarkMode
                ? 'linear-gradient(145deg,#1a1e2d,#1e2335)'
                : '#ffffff',
              borderRadius: '20px',
              width: '340px',
              maxWidth: '95vw',
              padding: '28px 24px',
              boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
              border: `1px solid ${isDarkMode ? '#2a2e45' : '#e0e3eb'}`,
              textAlign: 'center',
              position: 'relative'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎬</div>
              <h3 style={{ color: themeColors.text, marginBottom: '6px', fontSize: '17px', fontWeight: 700 }}>Select Replay Mode</h3>
              <p style={{ color: themeColors.muted, fontSize: '12px', marginBottom: '8px' }}>Choose how you want to start the session</p>
              {/* Session attempt counter for free users */}
              {!isPremium && (() => {
                const used = savedSessions.length;
                const remaining = Math.max(0, FREE_SESSION_LIMIT - used);
                return (
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    background: remaining <= 1 ? 'rgba(242,54,69,0.12)' : 'rgba(41,98,255,0.10)',
                    color: remaining <= 1 ? '#f23645' : '#4c82ff',
                    borderRadius: '20px', padding: '4px 12px',
                    fontSize: '11px', fontWeight: 700, marginBottom: '16px'
                  }}>
                    🎟 {remaining} session slot{remaining !== 1 ? 's' : ''} remaining (Free Plan)
                  </div>
                );
              })()}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <button
                  style={{ ...placeBuyBtn, borderRadius: '10px', padding: '14px', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                  onClick={() => handleModeChoice('manual')}
                >
                  <span style={{ fontSize: '18px' }}>🖱️</span> MANUAL (Drag the line)
                </button>
                <button
                  style={{ ...placeBuyBtn, backgroundColor: isDarkMode ? '#2962ff' : '#2962ff', borderRadius: '10px', padding: '14px', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                  onClick={() => handleModeChoice('auto')}
                >
                  <span style={{ fontSize: '18px' }}>🤖</span> AUTO (Start from random)
                </button>
                <button
                  style={{ background: 'none', border: 'none', color: themeColors.muted, marginTop: '8px', fontSize: '13px', cursor: 'pointer' }}
                  onClick={() => setShowModeSelection(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🗄️ SESSION POPUP – shown on chart open if sessions exist */}
        {showSessionPopup && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.75)',
            zIndex: 999999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(6px)'
          }}>
            <div style={{
              background: isDarkMode
                ? 'linear-gradient(145deg,#131722,#1a1e2a)'
                : '#ffffff',
              borderRadius: '22px',
              width: '400px',
              maxWidth: '95vw',
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 30px 80px rgba(0,0,0,0.75)',
              border: `1px solid ${isDarkMode ? '#2a2e45' : '#e0e3eb'}`,
              overflow: 'hidden'
            }}>
              {/* Header */}
              <div style={{
                padding: '22px 24px 16px',
                background: isDarkMode
                  ? 'linear-gradient(135deg,#1e2235,#252a3d)'
                  : '#f8f9fb',
                borderBottom: `1px solid ${isDarkMode ? '#2a2e45' : '#e0e3eb'}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '42px', height: '42px', borderRadius: '12px',
                    background: 'linear-gradient(135deg,#2962ff,#1a47c8)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '20px', flexShrink: 0
                  }}>📊</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '16px', color: themeColors.text }}>Saved Sessions</div>
                    <div style={{ fontSize: '12px', color: themeColors.muted, marginTop: '2px' }}>Resume a previous replay or start fresh</div>
                  </div>
                  <button
                    onClick={() => setShowSessionPopup(false)}
                    style={{ background: 'transparent', border: 'none', color: themeColors.muted, fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}
                  >✕</button>
                </div>
              </div>

              {/* Session List */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                {savedSessions.length === 0 ? (
                  <div style={{ textAlign: 'center', color: themeColors.muted, padding: '30px 0', fontSize: '13px' }}>No saved sessions</div>
                ) : (
                  savedSessions.map((session) => {
                    const timeLeft = SessionDB.getExpiryMs(session, isPremium);
                    const expireSoon = timeLeft < 15 * 60 * 1000; // < 15 min
                    return (
                      <div
                        key={session.id}
                        style={{
                          background: isDarkMode ? '#1e2235' : '#f8f9fb',
                          borderRadius: '14px',
                          padding: '16px',
                          marginBottom: '12px',
                          border: `1.5px solid ${isDarkMode ? '#2a2e45' : '#e0e3eb'}`,
                          transition: 'border-color 0.2s'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '15px', color: themeColors.text }}>
                              {session.symbol}
                            </div>
                            <div style={{ fontSize: '11px', color: themeColors.muted, marginTop: '3px' }}>
                              {new Date(session.savedAt).toLocaleString()}
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                            <span style={{
                              fontSize: '10px',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              background: session.mode === 'auto'
                                ? 'rgba(41,98,255,0.15)'
                                : 'rgba(8,153,129,0.15)',
                              color: session.mode === 'auto' ? '#4c82ff' : '#089981',
                              fontWeight: 700
                            }}>
                              {session.mode === 'auto' ? '🤖 AUTO' : '🖱️ MANUAL'}
                            </span>
                            <span style={{
                              fontSize: '10px',
                              color: expireSoon ? '#f23645' : themeColors.muted
                            }}>
                              ⏱ {SessionDB.formatTimeLeft(timeLeft)} left
                            </span>
                          </div>
                        </div>

                        <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: themeColors.muted, marginBottom: '12px' }}>
                          <span>💰 Balance: <strong style={{ color: themeColors.text }}>${session.balance?.toFixed(2) ?? '—'}</strong></span>
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => restoreSession(session)}
                            style={{
                              flex: 2,
                              padding: '9px',
                              borderRadius: '9px',
                              border: 'none',
                              background: 'linear-gradient(135deg,#2962ff,#1a47c8)',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '13px',
                              cursor: 'pointer',
                              transition: '0.2s'
                            }}
                          >
                            ▶ Resume
                          </button>
                          <button
                            onClick={() => deleteSession(session.id)}
                            style={{
                              flex: 1,
                              padding: '9px',
                              borderRadius: '9px',
                              border: `1px solid ${isDarkMode ? '#434651' : '#d1d4dc'}`,
                              background: 'transparent',
                              color: '#f23645',
                              fontWeight: 600,
                              fontSize: '13px',
                              cursor: 'pointer',
                              transition: '0.2s'
                            }}
                          >
                            🗑 Delete
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div style={{
                padding: '14px 16px',
                borderTop: `1px solid ${isDarkMode ? '#2a2e45' : '#e0e3eb'}`,
                background: isDarkMode ? '#131722' : '#f8f9fb'
              }}>
                <button
                  onClick={() => setShowSessionPopup(false)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: `1px solid ${isDarkMode ? '#434651' : '#d1d4dc'}`,
                    background: 'transparent',
                    color: themeColors.text,
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  ✕ Start New Session
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 💎 PREMIUM UPGRADE MODAL – shown when free user hits session limit */}
        {showPremiumModal && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.82)',
            zIndex: 999999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(8px)'
          }}>
            <div style={{
              background: isDarkMode
                ? 'linear-gradient(145deg,#131722,#1a1e2a)'
                : '#ffffff',
              borderRadius: '24px',
              width: '380px',
              maxWidth: '95vw',
              padding: '32px 26px',
              boxShadow: '0 30px 80px rgba(0,0,0,0.75)',
              border: `1.5px solid #f7931a44`,
              textAlign: 'center'
            }}>
              {/* Crown icon */}
              <div style={{
                width: '60px', height: '60px', borderRadius: '50%',
                background: 'linear-gradient(135deg,#f7931a,#e67e00)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '26px', margin: '0 auto 16px'
              }}>👑</div>

              <div style={{ fontWeight: 800, fontSize: '18px', color: themeColors.text, marginBottom: '6px' }}>
                Session Limit Reached
              </div>
              <div style={{ fontSize: '13px', color: themeColors.muted, marginBottom: '24px', lineHeight: 1.6 }}>
                Free users can save up to <strong style={{ color: '#f7931a' }}>{FREE_SESSION_LIMIT} active sessions</strong>.
                <br />Upgrade to Premium for unlimited sessions.
              </div>

              {/* Feature comparison */}
              <div style={{
                background: isDarkMode ? '#1e2235' : '#f8f9fb',
                borderRadius: '14px', padding: '16px', marginBottom: '20px',
                border: `1px solid ${isDarkMode ? '#2a2e45' : '#e0e3eb'}`,
                textAlign: 'left'
              }}>
                {[
                  { icon: '♾️', text: 'Unlimited replay sessions' },
                  { icon: '⏱', text: '24-hour session expiry (vs 2h)' },
                  { icon: '🤖', text: 'Auto & Manual replay modes' },
                  { icon: '💾', text: 'Auto-save & restore sessions' },
                ].map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: i < 3 ? '10px' : 0 }}>
                    <span style={{ fontSize: '16px' }}>{f.icon}</span>
                    <span style={{ fontSize: '13px', color: themeColors.text, fontWeight: 500 }}>{f.text}</span>
                    <span style={{ marginLeft: 'auto', color: '#089981', fontSize: '13px' }}>✓</span>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  onClick={() => {
                    setShowPremiumModal(false);
                    // Navigate to pricing page if available
                    try {
                      if (window.AndroidBridge?.openPricingPage) {
                        window.AndroidBridge.openPricingPage();
                      } else {
                        window.open('pricing.html', '_blank');
                      }
                    } catch (_) { }
                  }}
                  style={{
                    width: '100%', padding: '14px', borderRadius: '12px', border: 'none',
                    background: 'linear-gradient(135deg,#f7931a,#e67e00)',
                    color: 'white', fontWeight: 700, fontSize: '15px', cursor: 'pointer'
                  }}
                >
                  👑 Upgrade to Premium
                </button>
                <button
                  onClick={() => setShowPremiumModal(false)}
                  style={{
                    background: 'none', border: 'none',
                    color: themeColors.muted, fontSize: '13px', cursor: 'pointer', padding: '6px'
                  }}
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CUSTOM TOAST ALERT */}
        {replayAlert.show && (
          <div style={{
            position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999,
            backgroundColor: '#f23645', color: 'white', padding: '12px 24px', borderRadius: '8px',
            fontSize: '14px', fontWeight: 'bold', boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
            animation: 'fadeInOut 3s ease-in-out', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '10px'
          }}>
            <span style={{ fontSize: '18px' }}>⚠️</span>
            {replayAlert.message}
          </div>
        )}

        {/* 3. ORDER & POSITIONS MODAL */}
        {showOrderModal && (
          <div style={modalOverlay}>
            <div style={{ ...modalContent, backgroundColor: isDarkMode ? '#1e222d' : '#ffffff', color: themeColors.text }}>
              <div style={modalHeader}>
                <div style={mainTabGroup}>
                  <button style={activeTab === 'order' ? activeMainTab : { ...inactiveMainTab, color: themeColors.muted }} onClick={() => setActiveTab('order')}>New Order</button>
                  <button style={activeTab === 'positions' ? activeMainTab : { ...inactiveMainTab, color: themeColors.muted }} onClick={() => setActiveTab('positions')}>Trades ({openTrades.length})</button>
                </div>
                <button onClick={() => setShowOrderModal(false)} style={{ ...closeBtn, color: themeColors.text }}>✕</button>
              </div>

              {activeTab === 'order' ? (
                <div>
                  {/* Order Type Selector */}
                  <div style={{ ...tabContainer, marginBottom: '15px' }}>
                    <button
                      style={orderType === "market" ? activeBuyTab : inactiveTab}
                      onClick={() => setOrderType("market")}
                    >
                      MARKET
                    </button>

                    <button
                      style={orderType === "limit" ? activeSellTab : inactiveTab}
                      onClick={() => setOrderType("limit")}
                    >
                      LIMIT
                    </button>
                  </div>
                  <div style={tabContainer}>
                    <button style={side === 1 ? activeBuyTab : inactiveTab} onClick={() => setSide(1)}>BUY</button>
                    <button style={side === -1 ? activeSellTab : inactiveTab} onClick={() => setSide(-1)}>SELL</button>
                  </div>
                  <div style={inputRow}>
                    <div style={{ flex: 1 }}>
                      <label style={{ ...labelStyle, color: themeColors.muted }}>Lots</label>

                      <input
                        style={{
                          ...inputStyle,
                          backgroundColor: isDarkMode ? '#2a2e39' : '#ffffff',
                          color: themeColors.text,
                          borderColor: themeColors.border,
                        }}
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={lotSize}
                        onChange={(e) => {
                          const val = e.target.value;

                          // allow empty while typing
                          if (val === '') {
                            setLotSize('');
                            return;
                          }

                          const num = parseFloat(val);

                          if (!isNaN(num) && num > 0) {
                            setLotSize(num);
                          }
                        }}
                        onBlur={() => {
                          // restore default if left empty
                          if (!lotSize || lotSize <= 0) {
                            setLotSize(0.10);
                          }
                        }}
                      />
                    </div>


                    {/* Leverage */}
                    <div style={{ flex: 1 }}>
                      <label style={{ ...labelStyle, color: themeColors.muted }}>Leverage</label>
                      <select
                        style={{
                          ...selectStyle,
                          backgroundColor: isDarkMode ? '#2a2e39' : '#ffffff',
                          color: themeColors.text,
                          borderColor: themeColors.border
                        }}
                        value={leverage}
                        onChange={(e) => setLeverage(parseFloat(e.target.value))}
                      >
                        <option value="2">2x</option>
                        <option value="5">5x</option>
                        <option value="50">50x</option>
                        <option value="100">100x</option>
                        <option value="500">500x</option>
                        <option value="1000">1000x</option>
                      </select>
                    </div>
                  </div>

                  {/* ⭐ DYNAMIC COST MODEL (PRO BROKER UI) */}
                  {(uiAssetType === "forex" || uiAssetType === "crypto") && (
                    <div style={inputRow}>
                      <div style={{ flex: 1 }}>
                        <label style={{ ...labelStyle, color: themeColors.muted }}>
                          Spread
                        </label>

                        <input
                          style={{
                            ...inputStyle,
                            backgroundColor: isDarkMode ? '#2a2e39' : '#ffffff',
                            color: themeColors.text,
                            borderColor: themeColors.border
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          value={spread ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;

                            // Allow empty (user can remove default)
                            if (val === '') {
                              setSpread('');
                              return;
                            }

                            // Only allow valid number
                            const num = parseFloat(val);
                            if (!isNaN(num) && num >= 0) {
                              setSpread(num);
                            }
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }} />
                    </div>
                  )}

                  {(uiAssetType === "stock" || uiAssetType === "index") && (
                    <div style={inputRow}>
                      <div style={{ flex: 1 }}>
                        <label style={{ ...labelStyle, color: themeColors.muted }}>
                          Commission (%)
                        </label>

                        <input
                          style={{
                            ...inputStyle,
                            backgroundColor: isDarkMode ? '#2a2e39' : '#ffffff',
                            color: themeColors.text,
                            borderColor: themeColors.border
                          }}
                          type="number"
                          step="0.01"
                          min="0"
                          value={commission ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;

                            // Allow empty (user custom input)
                            if (val === '') {
                              setCommission('');
                              return;
                            }

                            const num = parseFloat(val);
                            if (!isNaN(num) && num >= 0) {
                              setCommission(num);
                            }
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }} />
                    </div>
                  )}

                  {orderType === "limit" && (
                    <div style={inputRow}>
                      <div style={{ flex: 1 }}>
                        <label style={{ ...labelStyle, color: themeColors.muted }}>
                          Limit Price
                        </label>

                        <input
                          style={{
                            ...inputStyle,
                            backgroundColor: isDarkMode ? '#2a2e39' : '#ffffff',
                            color: themeColors.text,
                            borderColor: themeColors.border
                          }}
                          type="text"
                          inputMode="decimal"
                          placeholder="Enter price"
                          value={limitPrice}
                          onChange={(e) => {
                            const val = e.target.value;

                            // Allow only numbers and one dot
                            if (/^\d*\.?\d*$/.test(val)) {
                              setLimitPrice(val);
                            }
                          }}
                          onBlur={() => {
                            // If left empty, keep empty (no force 0)
                            if (limitPrice === '') return;

                            const num = parseFloat(limitPrice);
                            if (!isNaN(num)) {
                              setLimitPrice(num.toFixed(2));
                            }
                          }}
                        />
                      </div>

                      <div style={{ flex: 1 }} />
                    </div>
                  )}



                  <div style={inputRow}>
                    <div style={{ flex: 1 }}>
                      <label style={{ ...labelStyle, color: '#089981' }}>Take Profit</label>
                      <input
                        style={{ ...inputStyle, backgroundColor: isDarkMode ? '#2a2e39' : '#ffffff', color: themeColors.text, borderColor: themeColors.border }}
                        type="number"
                        step="0.01"
                        placeholder="Optional"
                        value={tpInput || (side === 1 ? (currentReplayPrice * 1.01).toFixed(2) : (currentReplayPrice * 0.99).toFixed(2))}
                        onChange={(e) => setTpInput(e.target.value)}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ ...labelStyle, color: '#f23645' }}>Stop Loss</label>
                      <input
                        style={{ ...inputStyle, backgroundColor: isDarkMode ? '#2a2e39' : '#ffffff', color: themeColors.text, borderColor: themeColors.border }}
                        type="number"
                        step="0.01"
                        placeholder="Optional"
                        value={slInput || (side === 1 ? (currentReplayPrice * 0.99).toFixed(2) : (currentReplayPrice * 1.01).toFixed(2))}
                        onChange={(e) => setSlInput(e.target.value)}
                      />
                    </div>
                  </div>
                  {(() => {
                    const price =
                      orderType === "market"
                        ? currentReplayPrice
                        : (limitPrice && parseFloat(limitPrice) > 0
                          ? parseFloat(limitPrice)
                          : currentReplayPrice);

                    const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol || "XAUUSD";
                    const activeSymbol = normalizeSymbol(rawSymbol);

                    const lotValue = LOT_MULTIPLIER[activeSymbol] ?? 1;

                    const reqMargin =
                      (price * lotSize * lotValue) / leverage;

                    const displayMargin = toDisplayAmount(reqMargin);

                    return (
                      <div
                        style={{
                          backgroundColor: isDarkMode ? "rgba(41, 98, 255, 0.05)" : "#f0f3fa",
                          padding: "12px",
                          borderRadius: "4px",
                          marginBottom: "15px",
                          border: balance >= displayMargin ? `1px dashed ${themeColors.border}` : "1px solid #f23645"
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '11px', color: themeColors.muted }}>Required Margin:</span>
                          <span style={{ fontSize: '14px', fontWeight: 'bold', color: balance >= displayMargin ? themeColors.text : '#f23645' }}>
                            {currSymbol}{displayMargin.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  <button style={side === 1 ? placeBuyBtn : placeSellBtn} onClick={handlePlaceOrder}>
                    Place {side === 1 ? "Buy" : "Sell"}
                  </button>
                </div>
              ) : (
                <div style={tradesListScroll}>
                  {openTrades.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: themeColors.muted }}>No active trades</div>
                  ) : (
                    openTrades.map((trade) => {
                      const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol || "XAUUSD";
                      const activeSymbol = normalizeSymbol(rawSymbol);
                      const lotValue = LOT_MULTIPLIER[activeSymbol] ?? 1;
                      // 🔥 REALISTIC PnL (CRITICAL FIX)
                      // Pending LIMIT orders must NOT show floating PnL
                      let rawPnl = 0;
                      if (trade.isExecuted) {
                        rawPnl =
                          (currentReplayPrice - trade.price) *
                          trade.side *
                          trade.qty *
                          lotValue;

                        const assetType = getAssetType(activeSymbol);
                        if (assetType === "stock" || assetType === "index") {
                          const tradeValue = trade.price * trade.qty * lotValue;
                          const commissionCost = (tradeValue * (commission === '' ? 0.2 : commission)) / 100;
                          rawPnl -= commissionCost;
                        }
                      }

                      const pnl = toDisplayAmount(rawPnl);

                      const isProfit = pnl >= 0;
                      return (
                        <div key={trade.id} style={{ ...tradeItemStyle, backgroundColor: isDarkMode ? '#2a2e39' : '#f8f9fb', borderBottom: `1px solid ${themeColors.border}`, flexDirection: 'column', alignItems: 'stretch', gap: '8px', padding: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: trade.side === 1 ? '#089981' : '#f23645', fontWeight: 'bold', fontSize: '13px' }}>
                              {trade.side === 1 ? "LONG" : "SHORT"} {trade.qty} Lots ({trade.leverage}x)
                              {!trade.isExecuted && (
                                <span style={{ color: '#f7931a', marginLeft: '6px', fontSize: '11px' }}>
                                  • PENDING
                                </span>
                              )}
                            </span>
                            <span style={{ color: isProfit ? '#089981' : '#f23645', fontWeight: 'bold', fontSize: '15px' }}>
                              {isProfit ? "+" : ""}{currSymbol}{pnl.toFixed(2)}
                            </span>
                          </div>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 1fr',
                              gap: '8px',
                              fontSize: '11px',
                              color: themeColors.muted
                            }}
                          >
                            <div>
                              Entry: <span style={{ color: themeColors.text }}>
                                {trade.price.toFixed(2)}
                              </span>
                            </div>

                            <div>
                              Current: <span style={{ color: themeColors.text }}>
                                {currentReplayPrice.toFixed(2)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: themeColors.muted }}>TP:</span>
                              {trade.tpPrice != null ? (
                                <span style={{ color: '#089981' }}>
                                  {Number(trade.tpPrice).toFixed(2)}
                                </span>
                              ) : (
                                <button
                                  title="Restore Take Profit"
                                  onClick={() => {
                                    const defaultTP = trade.side === 1
                                      ? currentReplayPrice * 1.01
                                      : currentReplayPrice * 0.99;
                                    setOpenTrades(prev =>
                                      prev.map(t =>
                                        t.id === trade.id
                                          ? { ...t, tpPrice: parseFloat(defaultTP.toFixed(2)) }
                                          : t
                                      )
                                    );
                                  }}
                                  style={{
                                    background: '#089981', border: 'none', color: 'white',
                                    borderRadius: '4px', width: '18px', height: '18px',
                                    fontSize: '13px', cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 'bold', flexShrink: 0,
                                  }}
                                >+</button>
                              )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: themeColors.muted }}>SL:</span>
                              {trade.slPrice != null ? (
                                <span style={{ color: '#f23645' }}>
                                  {Number(trade.slPrice).toFixed(2)}
                                </span>
                              ) : (
                                <button
                                  title="Restore Stop Loss"
                                  onClick={() => {
                                    const defaultSL = trade.side === 1
                                      ? currentReplayPrice * 0.99
                                      : currentReplayPrice * 1.01;
                                    setOpenTrades(prev =>
                                      prev.map(t =>
                                        t.id === trade.id
                                          ? { ...t, slPrice: parseFloat(defaultSL.toFixed(2)) }
                                          : t
                                      )
                                    );
                                  }}
                                  style={{
                                    background: '#f23645', border: 'none', color: 'white',
                                    borderRadius: '4px', width: '18px', height: '18px',
                                    fontSize: '13px', cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 'bold', flexShrink: 0,
                                  }}
                                >+</button>
                              )}
                            </div>

                            {/* ⭐ NEW: USED MARGIN (PRO FEATURE) */}
                            <div>
                              Margin: <span style={{ color: '#2962ff', fontWeight: 'bold' }}>
                                {currSymbol}{toDisplayAmount(trade.marginUsed).toFixed(2)}
                              </span>
                            </div>

                            {/* ⭐ OPTIONAL: Order Type (very professional) */}
                            <div>
                              Type: <span style={{ color: themeColors.text }}>
                                {trade.orderType === "limit" ? "LIMIT" : "MARKET"}
                              </span>
                            </div>
                          </div>
                          <button
                            style={{
                              ...closeTradeBtn,
                              width: '100%',
                              padding: '8px',
                              marginTop: '5px',
                              backgroundColor:
                                trade.orderType === "limit" && !trade.isExecuted
                                  ? "#f23645"   // red for cancel
                                  : (isDarkMode ? '#363a45' : '#e0e3eb'),
                              color:
                                trade.orderType === "limit" && !trade.isExecuted
                                  ? "#ffffff"
                                  : themeColors.text,
                              fontWeight: "bold"
                            }}
                            onClick={() => {

                              // 🔥 CASE 1: LIMIT ORDER NOT EXECUTED → CANCEL ORDER (X)
                              if (trade.orderType === "limit" && !trade.isExecuted) {

                                // Remove only entry line (TP/SL don't exist yet)
                                trade.tvLines?.entryLine?.remove?.();
                                trade.tvLines?.tpLine?.remove?.();
                                trade.tvLines?.slLine?.remove?.();

                                // Refund margin ONLY if somehow deducted (safety)
                                setBalance(prev => {
                                  const newBal = prev + (trade.marginUsed || 0);

                                  if (window.AndroidBridge?.saveNewAmount) {
                                    window.AndroidBridge.saveNewAmount(newBal);
                                  }

                                  return newBal;
                                });

                                // Remove trade from UI (cancel)
                                setOpenTrades(prev => prev.filter(t => t.id !== trade.id));
                                setIsPlaying(false); // ⬅️ ADD THIS
                                setClosedTradeData({
                                  symbol: activeSymbol,
                                  side: trade.side,
                                  entry: trade.price,
                                  exit: exitPrice,
                                  tp: tpPrice,
                                  sl: slPrice,
                                  lots: trade.qty,
                                  leverage: trade.leverage,
                                  pnl: finalPnl,
                                  closeReason: "MANUAL_CLOSE",
                                  marginUsed: trade._lockedMargin ?? trade.marginUsed ?? 0,
                                  currency: selectedCurrency,
                                });
                                return; // 🛑 IMPORTANT: stop here (do not run close logic)
                              }

                              // 🔥 CASE 2: LIVE TRADE → NORMAL CLOSE POSITION
                              const entryPrice = trade.price;
                              const slPrice = trade.tvLines?.slLine?.getPrice?.() ?? null;
                              const tpPrice = trade.tvLines?.tpLine?.getPrice?.() ?? null;

                              const rawSymbol = tvWidgetRef.current?.symbolInterval()?.symbol || "XAUUSD";
                              const activeSymbol = normalizeSymbol(rawSymbol);

                              const lotValue = LOT_MULTIPLIER[activeSymbol] ?? 1;

                              // Manual close should use current market price
                              // Manual close should use current market price
                              const exitPrice = currentReplayPrice;

                              let rawPnl = 0;
                              if (trade.isExecuted) {
                                rawPnl =
                                  (exitPrice - trade.price) *
                                  trade.side *
                                  trade.qty *
                                  lotValue;

                                // ⭐ APPLY COMMISSION (CRITICAL FIX)
                                const assetType = getAssetType(activeSymbol);
                                if (assetType === "stock" || assetType === "index") {
                                  const tradeValue = trade.price * trade.qty * lotValue;
                                  const commissionCost = (tradeValue * (commission === '' ? 0.2 : commission)) / 100;
                                  rawPnl -= commissionCost;
                                }
                              }
                              const finalPnl = toDisplayAmount(rawPnl);

                              // Get replay candle time
                              const list = window.barCache["1"] || [];
                              const idx = window.replayIndex;
                              const tradeBarTime = list[idx]?.time ?? Date.now();

                              const tradeDateObj = new Date(tradeBarTime);
                              const tradeDate = tradeDateObj.toISOString().split('T')[0];
                              const tradeTime = tradeDateObj.toTimeString().split(' ')[0];

                              function getWeekNumber(date) {
                                const tempDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
                                const dayNum = tempDate.getUTCDay() || 7;
                                tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
                                const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
                                return Math.ceil((((tempDate - yearStart) / 86400000) + 1) / 7);
                              }

                              const weekOfYear = getWeekNumber(tradeDateObj);
                              const year = tradeDateObj.getFullYear();

                              // 🚫 Anti-cheat: DO NOT send manual replay trades to Android
                              const manualBridge = window.AndroidBridge || window.WebBridge;
                              if (
                                replayModeTypeRef.current === 'auto' &&
                                manualBridge?.onManualClose
                              ) {
                                const currencyCode = selectedCurrency;
                                manualBridge.onManualClose(
                                  finalPnl,
                                  currencyCode,
                                  tradeDate,
                                  year,
                                  weekOfYear,
                                  tradeTime,
                                  entryPrice,
                                  slPrice ?? 0,
                                  tpPrice ?? 0
                                );
                              }
                              // 🔔 MANUAL CLOSE NOTIFICATION
                              showChartNotification(
                                `Position Closed | PnL: ${currSymbol}${finalPnl.toFixed(2)}`,
                                finalPnl >= 0 ? "success" : "error"
                              );
                              // Remove chart lines safely
                              trade.tvLines?.entryLine?.remove?.();
                              trade.tvLines?.tpLine?.remove?.();
                              trade.tvLines?.slLine?.remove?.();
                              if (
                                replayModeTypeRef.current === "auto" &&
                                manualBridge?.saveTradeToHistory
                              ) {
                                const realSL = trade.tvLines?.slLine?.getPrice?.() ?? null;
                                const realTP = trade.tvLines?.tpLine?.getPrice?.() ?? null;
                                const usedMargin = trade._lockedMargin ?? trade.marginUsed ?? 0;

                                // ✅ HOLD TIME CALCULATION
                                const closeTime = Date.now();
                                const holdDurationMs = closeTime - (trade.openTime ?? closeTime);
                                const holdSeconds = Math.floor(holdDurationMs / 1000);
                                const holdMinutes = Math.floor(holdSeconds / 60);
                                const holdHours = Math.floor(holdMinutes / 60);
                                const holdDuration =
                                  holdHours > 0
                                    ? `${holdHours}h ${holdMinutes % 60}m`
                                    : holdMinutes > 0
                                      ? `${holdMinutes}m ${holdSeconds % 60}s`
                                      : `${holdSeconds}s`;

                                const tradeData = {
                                  symbol: activeSymbol,
                                  type: trade.side === 1 ? "BUY" : "SELL",
                                  status: "MANUAL_CLOSE",
                                  pnl: Number(finalPnl.toFixed(2)),
                                  entry: Number(trade.price),
                                  exit: Number(exitPrice),
                                  lots: Number(trade.qty),
                                  leverage: `${trade.leverage}x`,
                                  sl: realSL !== null ? Number(realSL) : null,
                                  tp: realTP !== null ? Number(realTP) : null,
                                  marginUsed: Number(usedMargin),
                                  timestamp: Date.now(),
                                  openTime: Number(trade.openTime ?? closeTime),   // ✅ ADD
                                  closeTime: Number(closeTime),                    // ✅ ADD
                                  holdDurationMs: Number(holdDurationMs),          // ✅ ADD
                                  holdDuration: holdDuration,                      // ✅ ADD
                                };

                                manualBridge.saveTradeToHistory(JSON.stringify(tradeData));
                              }
                              // Refund margin + PnL
                              const newBalance = balance + trade.marginUsed + finalPnl;
                              setBalance(newBalance);

                              if (manualBridge?.saveNewAmount) {
                                manualBridge.saveNewAmount(newBalance);
                              }

                              setOpenTrades(prev => prev.filter(t => t.id !== trade.id));
                            }}
                          >
                            {trade.orderType === "limit" && !trade.isExecuted
                              ? "❌ Cancel Order"
                              : "Close Position"}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showBalanceModal && (
        <div style={modalOverlay}>
          <div style={{ ...modalContent, textAlign: 'center', backgroundColor: isDarkMode ? '#1e222d' : '#ffffff' }}>
            <h3 style={{ marginBottom: '15px', color: themeColors.text }}>
              {balanceAction === "add" ? "Add Balance" : "Withdraw Balance"}
            </h3>

            <input
              type="number"
              placeholder="Enter amount"
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(e.target.value)}
              style={{ ...inputStyle, backgroundColor: isDarkMode ? '#2a2e39' : '#ffffff', color: themeColors.text }}
            />

            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
              <button
                style={placeBuyBtn}
                onClick={() => {
                  const amount = parseFloat(balanceAmount);

                  if (isNaN(amount) || amount <= 0) {
                    alert("Enter valid amount");
                    return;
                  }

                  if (balanceAction === "withdraw" && amount > balance) {
                    alert("Insufficient balance");
                    return;
                  }

                  const newBalance =
                    balanceAction === "add"
                      ? balance + amount
                      : balance - amount;

                  updateBalanceSafe(newBalance);

                  setBalanceAmount("");
                  setShowBalanceModal(false);
                }}
              >
                Confirm
              </button>

              <button
                style={closeTradeBtn}
                onClick={() => setShowBalanceModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* EXIT CONFIRM MODAL */}
      {showExitConfirm && (
        <div style={modalOverlay}>
          <div style={{
            ...modalContent,
            backgroundColor: isDarkMode ? '#1e222d' : '#ffffff',
            textAlign: 'center'
          }}>
            <h3 style={{ color: themeColors.text, marginBottom: '15px' }}>
              Exit Replay?
            </h3>

            <p style={{ color: themeColors.muted, fontSize: '13px', marginBottom: '20px' }}>
              Your session is auto-saved. You can resume it next time you open the chart.
            </p>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                style={placeSellBtn}
                onClick={async () => {
                  setShowExitConfirm(false);
                  // 💾 Save session before exit
                  await autoSaveSession();
                  newsIndexRef.current = 0;
                  // Clear current session id so it doesn't get overwritten
                  if (currentSessionIdRef.current) {
                    await SessionDB.delete(currentSessionIdRef.current);
                    currentSessionIdRef.current = null;
                  }
                  resetReplay();
                  window.location.reload();
                }}
              >
                Exit Without Saving
              </button>

              <button
                style={placeBuyBtn}
                onClick={async () => {
                  setShowExitConfirm(false);
                  // 💾 Save final session snapshot
                  await autoSaveSession();
                  triggerAlert('✅ Session saved!');
                  setTimeout(() => {
                    newsIndexRef.current = 0;
                    resetReplay();
                    window.location.reload();
                  }, 1200);
                }}
              >
                💾 Save &amp; Exit
              </button>

              <button
                style={closeTradeBtn}
                onClick={() => setShowExitConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. BOTTOM TRADE BAR (ONLY REPLAY ACTIVE) */}
      {isReplayUIVisible && (
        <div style={{ ...bottomTradeBarStyle, backgroundColor: themeColors.header, borderTop: `1px solid ${themeColors.border}` }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <div style={accountInfo}>
              <span style={{ ...accLabel, color: themeColors.muted }}>BALANCE:</span>
              <span style={{ ...accValue, color: themeColors.text }}>
                {currSymbol}{toDisplayAmount(balance).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                style={{ fontSize: '10px', padding: '4px 6px', background: '#089981', color: 'white', border: 'none', borderRadius: '4px' }}
                onClick={() => {
                  setBalanceAction("add");
                  setShowBalanceModal(true);
                }}
              >
                + Add
              </button>

              <button
                style={{ fontSize: '10px', padding: '4px 6px', background: '#f23645', color: 'white', border: 'none', borderRadius: '4px' }}
                onClick={() => {
                  setBalanceAction("withdraw");
                  setShowBalanceModal(true);
                }}
              >
                − Withdraw
              </button>
            </div>
          </div>
          <div className="currency-dropdown-wrapper" style={{ position: 'relative', marginLeft: '8px' }}>
            {/* Trigger Button */}
            <button
              onClick={() => setShowCurrencyDropdown(prev => !prev)}
              style={{
                padding: '4px 10px', fontSize: '12px', borderRadius: '4px',
                border: `1px solid ${themeColors.border}`, backgroundColor: themeColors.bg,
                color: themeColors.text, cursor: 'pointer', fontWeight: 'bold',
                display: 'flex', alignItems: 'center', gap: '4px'
              }}
            >
              {currSymbol} {selectedCurrency} ▾
            </button>

            {/* Dropdown Panel */}
            {showCurrencyDropdown && (
              <div style={{
                position: 'absolute', bottom: '110%', left: 0,
                background: isDarkMode ? '#1e222d' : '#ffffff',
                border: `1px solid ${themeColors.border}`,
                borderRadius: '10px', zIndex: 99999,
                boxShadow: '0 -10px 30px rgba(0,0,0,0.4)',
                minWidth: '140px', overflow: 'hidden'
              }}>
                {Object.entries(CURRENCY_CONFIG).map(([code, cfg]) => (
                  <div
                    key={code}
                    onClick={() => {
                      setSelectedCurrency(code);
                      setShowCurrencyDropdown(false);
                    }}
                    style={{
                      padding: '9px 14px', fontSize: '13px', cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: selectedCurrency === code
                        ? (isDarkMode ? '#2962ff22' : '#2962ff11')
                        : 'transparent',
                      color: themeColors.text,
                      fontWeight: selectedCurrency === code ? 'bold' : '400'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = isDarkMode ? '#2a2e39' : '#f0f3fa'}
                    onMouseLeave={e => e.currentTarget.style.background = selectedCurrency === code
                      ? (isDarkMode ? '#2962ff22' : '#2962ff11') : 'transparent'}
                  >
                    <span>{cfg.symbol} {code}</span>
                    {selectedCurrency === code && <span style={{ color: '#2962ff' }}>✓</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={buttonGroupStyle}>
            <button style={tradeActionBtn} onClick={() => { setActiveTab('order'); setShowOrderModal(true); }}>NEW ORDER</button>
            <button style={{ ...positionsBtnStyle, backgroundColor: isDarkMode ? '#363a45' : '#e0e3eb', color: themeColors.text }} onClick={() => { setActiveTab('positions'); setShowOrderModal(true); }}>OPEN TRADES</button>
          </div>
        </div>
      )}
      <PostTradeAnalysis
        tradeData={closedTradeData}
        onClose={() => setClosedTradeData(null)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

const mainContainerStyle = { display: "flex", flexDirection: "column", height: "100vh", width: "100%", backgroundColor: "#131722", color: "#d1d4dc", overflow: 'hidden' };
const headerAreaStyle = { height: "50px", backgroundColor: "#1e222d", display: "flex", alignItems: "center", justifyContent: "center", gap: "15px", borderBottom: "1px solid #434651" };
const chartWrapperStyle = { flex: 1, position: "relative" };
const bottomTradeBarStyle = { height: "60px", backgroundColor: "#1e222d", borderTop: "1px solid #434651", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 15px" };
const modalOverlay = { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1000, display: "flex", justifyContent: "center", alignItems: "center" };
const modalContent = { backgroundColor: "#1e222d", width: "90%", maxWidth: "350px", padding: "20px", borderRadius: "8px", border: "1px solid #434651" };
const modalHeader = { display: "flex", justifyContent: "space-between", marginBottom: "20px" };
const mainTabGroup = { display: 'flex', gap: '10px' };
const inactiveMainTab = { background: 'none', border: 'none', color: '#868993', cursor: 'pointer', fontSize: '13px' };
const activeMainTab = { background: 'none', border: 'none', color: '#2962ff', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', borderBottom: '2px solid #2962ff' };
const closeBtn = { background: "none", border: "none", color: "#868993", cursor: "pointer", fontSize: "18px" };
const tabContainer = { display: "flex", borderRadius: "4px", overflow: "hidden", marginBottom: "20px", border: "1px solid #434651" };
const inactiveTab = { flex: 1, padding: "8px", border: "none", background: "#2a2e39", color: "#868993", cursor: "pointer" };
const activeBuyTab = { flex: 1, padding: "8px", border: "none", background: "#089981", color: "white", fontWeight: "bold" };
const activeSellTab = { flex: 1, padding: "8px", border: "none", background: "#f23645", color: "white", fontWeight: "bold" };
const tradesListScroll = { maxHeight: '300px', overflowY: 'auto' };
const tradeItemStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', borderBottom: '1px solid #2a2e39' };
const closeTradeBtn = { backgroundColor: '#363a45', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '11px' };
const inputGroup = { marginBottom: "15px" };
const inputRow = { display: "flex", gap: "10px", marginBottom: "20px" };
const labelStyle = { display: "block", fontSize: "11px", color: "#868993", marginBottom: "5px" };
const inputStyle = { width: "100%", backgroundColor: "#2a2e39", border: "1px solid #434651", color: "white", padding: "8px", borderRadius: "4px" };
const selectStyle = { ...inputStyle, cursor: "pointer" };
const placeBuyBtn = { width: "100%", padding: "12px", borderRadius: "4px", border: "none", backgroundColor: "#089981", color: "white", fontWeight: "bold" };
const placeSellBtn = { ...placeBuyBtn, backgroundColor: "#f23645" };
const buttonGroupStyle = { display: "flex", gap: "8px" };
const tradeActionBtn = { backgroundColor: "#2962ff", color: "white", border: "none", padding: "8px 12px", borderRadius: "4px", fontWeight: "bold" };
const positionsBtnStyle = { backgroundColor: "#363a45", color: "white", border: "none", padding: "8px 12px", borderRadius: "4px", fontWeight: "bold" };
const accountInfo = { display: "flex", gap: "5px", alignItems: "center" };
const accLabel = { color: "#868993", fontSize: "10px" };
const accValue = { fontSize: "14px", fontWeight: "bold", color: '#089981' };
const btnStyle = { background: "none", border: "none", color: "#d1d4dc", cursor: "pointer", fontWeight: "bold" };
const speedBtnStyle = { background: "#2a2e39", border: "1px solid #434651", color: "#2962ff", padding: "4px 10px", borderRadius: "4px", fontSize: '12px' };
const dividerStyle = { width: "1px", height: "20px", background: "#434651" };
const confirmBtnStyle = { background: "#2962ff", color: "white", border: "none", padding: "6px 12px", borderRadius: "4px", fontSize: '12px' };
const sliderStyle = {
  cursor: 'pointer',
  width: '80px',
  accentColor: '#2962ff',
  height: '4px',
  outline: 'none',
  WebkitAppearance: 'none',
  backgroundColor: '#434651',
  borderRadius: '2px'
};

export default TVChartContainer;